<template>
  <div class="h-full">
    <div v-if="loading" class="flex items-center justify-center h-full">
      <loading-spinner diameter="64px" />
    </div>

    <alert-badge
      v-else-if="error.title"
      isDanger
      :title="error.title"
      :body="error.body"
      @dismissed="
        () => {
          error.title = ''
          error.body = ''
        }
      "
    />
    <alert-badge
      v-for="err of stockQuantityErrors"
      class="mt-1"
      isDanger
      :title="err[1]"
      @dismissed="
        () => {
          stockQuantityErrors.delete(err[0])
        }
      "
    />

    <div class="flex bg-white mx-4">
      <div class="cart w-3/4 mt-10 px-6">
        <div class="header h-14 flex flex-1 justify-between items-start gap-2">
          <div class="search-box h-full w-full flex items-center">
            <BarcodeScanner v-if="!isQuote" :active="isActiveTab !== false" @scan="handleScan" v-model:is-scanning="isScanning" />
            <el-autocomplete
              v-model="itemSearchTerm"
              :fetch-suggestions="searchItems"
              :placeholder="t('searchItems')"
              @select="addItem"
              class="w-full h-full"
              value-key="name"
              @keypress.enter.prevent="onSearchSelectPressedEnter"
              @keyup.enter.prevent="onSearchSelectKeyupEnter"
              :trigger-on-focus="false"
              highlight-first-item
              :prefix-icon="Search"
              ref="searchInput"
            >
              <template #default="{ item }">
                <div
                  :class="{ 'flex-row-reverse': $ctx.locale == 'ar' }"
                  class="flex justify-between"
                >
                  <div>{{ item.name }}</div>
                  <div v-if="!item.unlimitedStock && !isQuote">
                    {{ getAvailableQuantity(item) }}
                  </div>
                </div>
              </template>
            </el-autocomplete>
          </div>
          <div v-if="table?.getSelectionRows().length > 0">
            <SpecialOrderItemDialog
              :orderItem="table?.getSelectionRows()[0]"
              @submit="(data) => onSpecialOrderItemSubmitted(table?.getSelectionRows()[0], data)"
              :isGroup="true"
            />
          </div>
          <div v-if="$can('get', '/inventory/items/cost')" class="flex items-center gap-1">
            <el-tooltip :content="t('costAndProfit')" placement="top">
              <button
                class="flex items-center justify-center px-2 py-1 rounded border text-sm transition-colors mt-1"
                :class="showCostAndProfit ? 'bg-[#1D4C75] text-white border-[#1D4C75]' : 'border-gray-300 text-gray-600 hover:border-[#1D4C75] hover:text-[#1D4C75]'"
                @click="showCostAndProfit = !showCostAndProfit"
              >
                <Icon icon="fa6-solid:hand-holding-dollar" width="16" height="16" />
              </button>
            </el-tooltip>
          </div>
          <div class="flex items-center">
            <el-switch
              v-model="appendToOrderItems"
              inline-prompt
              active-text="+"
              inactive-text="="
            />
          </div>
        </div>

        <div class="flex flex-row w-full overflow-x-auto pb-4" v-if="top8Items.length > 0 && !isQuote">
          <div
            v-for="item in top8Items"
            :key="item.id"
            class="bg-gray-100 p-2 mx-2 text-nowrap rounded cursor-pointer hover:bg-gray-400 hover:text-white select-none"
            @click="addItem(item)"
          >
            {{ item.name }}
          </div>
        </div>

        <div class="items-table w-full mt-6 overflow-y-scroll flex flex-col">
          <!--          <pre>{{table?.getSelectionRows().map((oi) => oi.selectID)}}</pre>-->
          <el-table
            :table-layout="'fixed'"
            :data="order.orderItems"
            stripe
            border
            style="width: 100%"
            height="100%"
            class="card rounded"
            header-cell-class-name="my-header"
            ref="table"
          >
            <!-- <el-table-column
              :min-width="40"
              type="index"
              :index="indexMethod"
              label="#"
            /> -->
            <el-table-column type="selection" width="55" :selectable="selectable" />

            <el-table-column :min-width="90" prop="item.externalID" :label="t('itemCode')">
              <template #default="scope">
                <div class="flex items-center justify-between">
                  <span>{{ scope.row.item.externalID }}</span>
                  <SpecialOrderItemDialog
                    :orderItem="scope.row"
                    @submit="(data) => onSpecialOrderItemSubmitted(scope.row, data)"
                  />
                </div>
              </template>
            </el-table-column>
            <el-table-column :min-width="150" :label="t('itemName')">
              <template #default="scope">
                <div class="flex items-center justify-between">
                  <div class="truncate">
                    <span
                      :class="{
                        'text-red-500 font-semibold': !isQuote && isOutOfStock(scope.row.item, scope.row.quantity) && !scope.row.specialOrderItemInfo
                      }"
                    >
                      {{ scope.row.item.name }}
                    </span>
                  </div>
                  <div style="width: 30px">
                    <span
                      v-if="scope.row.item.isSerialized && !isOrderFulfillmentMode"
                      @click="openSerialDialog(scope.row)"
                    >
                      <img src="../../assets/serials.png" alt="serials icon" class="item-serial" />
                    </span>
                  </div>
                  <div class="flex gap-2">
                    <CustomPriceInput
                      :item="scope.row.item"
                      :customPriceInfo="scope.row.customPriceInfo"
                      :packInfo="scope.row.packInfo"
                      @onCustomPrice="
                        (customPriceInfo) => {
                          onCustomPriceInfo(customPriceInfo, scope.row)
                        }
                      "
                    >
                    </CustomPriceInput>
                    <ItemDiscountDialog
                      v-if="canApplyDiscount"
                      :orderItem="scope.row"
                      @onItemDiscount="
                        (discountInfo) => {
                          onItemDiscount(discountInfo, scope.row)
                        }
                      "
                    />
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column v-if="settingsStore.getValue('expiration_module') === 'true'" :min-width="150" :label="t('batchNumber')">
              <template #default="scope">
                <el-select
                  v-if="scope.row.item.isExpirable && !isOrderFulfillmentMode"
                  v-model="scope.row.extraProperties.batchNumber"
                  :placeholder="t('batchNumber')"
                  @change="onBatchNumberChange(scope.row)"
                >
                  <template
                    v-for="stock in scope.row.item.itemStocks"
                    :key="stock.extraProperties?.batchNumber"
                  >
                    <el-option
                      v-if="stock.extraProperties?.batchNumber"
                      :label="stock.extraProperties?.batchNumber"
                      :value="stock.extraProperties?.batchNumber"
                    />
                  </template>
                </el-select>
              </template>
            </el-table-column>
            <el-table-column :min-width="120" prop="item.salePriceAmount" :label="t('price')">
              <template #default="scope">
                <div v-if="scope.row.item.priceType == 'static'">
                  {{
                    getDineroValue(
                      scope.row.itemSalePriceAmount ? scope.row.itemSalePriceAmount : 0,
                      scope.row.currency,
                      scope.row.precision
                    )
                  }}
                </div>
                <money-input
                  style="width: 110px"
                  v-if="scope.row.item.priceType == 'dynamic'"
                  id="list-price"
                  :currency="$ctx.currency"
                  v-model="scope.row.itemSalePriceAmount"
                  @dinero-created="
                    (dinero) => {
                      scope.row.itemSalePriceDinero = dinero
                    }
                  "
                  ref="salePriceAmountRef"
                />
                <div v-if="showCostAndProfit" class="text-xs text-gray-400 mt-0.5">
                  {{ formatMoney(scope.row.item.currentDynamicCostAmount || 0, scope.row.currency) }}
                </div>
              </template>
            </el-table-column>
            <el-table-column type="index" :min-width="145" prop="quantity" :label="t('quantity')">
              <template #default="scope">
                <div
                  :class="{
                    'border-2 border-red-500 rounded p-1': !isQuote && isOutOfStock(scope.row.item, scope.row.quantity) && !scope.row.specialOrderItemInfo
                  }"
                >
                  <!--                <el-input-number-->
                  <!--                  class="checkout-quantity-el-input-number"-->
                  <!--                  style="width: 115px"-->
                  <!--                  v-model="scope.row.quantity"-->
                  <!--                  :step="1"-->
                  <!--                  size="mini"-->
                  <!--                  :min="1"-->
                  <!--                ></el-input-number>-->
                  <QuantityInput
                    :item="scope.row.item"
                    :packInfo="scope.row.packInfo"
                    type="el-input-number"
                    :inputClass="isOutOfStock(scope.row.item, scope.row.quantity) && !scope.row.specialOrderItemInfo ? 'checkout-quantity-el-input-number text-red-500' : 'checkout-quantity-el-input-number'"
                    :usePackQuantity="true"
                    @onPack="
                      (packInfo) => {
                        onPack(packInfo, scope.row)
                      }
                    "
                    v-model="scope.row.quantity"
                  >
                  </QuantityInput>
                </div>
              </template>
            </el-table-column>
            <el-table-column :min-width="110" :label="t('total')">
              <template #default="scope">
                <div>{{ getItemTotal(scope.row) }}</div>
                <div v-if="showCostAndProfit" class="text-xs text-gray-400 mt-0.5">
                  {{ formatMoney((scope.row.item.currentDynamicCostAmount || 0) * scope.row.quantity, scope.row.currency) }}
                </div>
              </template>
            </el-table-column>
            <el-table-column :min-width="100" :label="t('notes')" align="center">
              <template #default="scope">
                <el-input id="notes" v-model="scope.row.notes" show-word-limit clearable>
                </el-input>
              </template>
            </el-table-column>
            <el-table-column :min-width="80" :label="t('delete')" align="center">
              <template #default="scope">
                <span class="text-red-600 cursor-pointer" @click="order.removeItem(scope.$index)">
                  <font-awesome-icon icon="trash" />
                </span>
              </template>
            </el-table-column>
          </el-table>
          <div class="mb-2 mt-2">
            <div>{{ t('notes') }}</div>
            <div>
              <el-input v-model="order.invoice.notes" :rows="2" type="textarea" placeholder="" />
            </div>
          </div>
        </div>
      </div>

      <div class="aside w-1/4 border-l-2 flex flex-col mt-10 px-4">
        <div class="order-info my-4 bg-white">
          <template v-if="!isQuote">
          <div class="grid grid-cols-5 justify-between">
            <h1 class="col-span-2">{{ t('orderType') }}</h1>
            <el-select
              class="col-span-3"
              value-key="id"
              v-model="order.orderType"
              @change="onOrderTypeChange"
              placeholder=""
            >
              <el-option
                v-for="option in orderTypes"
                :key="option.value"
                :label="t(option.label)"
                :value="option.value"
              ></el-option>
            </el-select>
          </div>
          <div v-if="order.orderType == 'delivery'" class="mt-2 grid grid-cols-5 justify-between">
            <h1 class="col-span-2">{{ t('carrier') }}</h1>
            <el-select
              class="col-span-3"
              value-key="id"
              v-model="order.invoice.carrierID"
              placeholder=""
              filterable
              clearable
            >
              <el-option
                v-for="option in deliveries"
                :key="option.id"
                :label="t(option.name)"
                :value="option.id"
              ></el-option>
            </el-select>
          </div>
          <hr class="mt-2" />
          </template>
          <!-- Quote mode: show quote fields instead of cashier/sales rep -->
          <template v-if="isQuote">
            <div class="mt-2 grid grid-cols-5 justify-between">
              <h1 class="col-span-2">{{ t('quotedBy') }}</h1>
              <div class="col-span-3 font-bold">{{ ctx.currentUser?.fullName || ctx.currentUser?.name || '' }}</div>
            </div>
            <div class="mt-2 grid grid-cols-5 justify-between">
              <h1 class="col-span-2">{{ t('paymentTerms') }}</h1>
              <el-select class="col-span-3" v-model="quotePaymentType" placeholder="">
                <el-option value="direct" :label="t('direct')" />
                <el-option value="credit_sale" :label="t('credit_sale')" />
                <el-option value="layaway" :label="t('layaway')" />
                <el-option value="cash_on_delivery" :label="t('cash_on_delivery')" />
              </el-select>
            </div>
            <div class="mt-2 grid grid-cols-5 justify-between w-full">
              <h1 class="col-span-2">{{ t('validUntil') }}</h1>
              <el-date-picker
                class="col-span-3"
                style="width: 100%"
                v-model="quoteValidUntil"
                type="date"
                :placeholder="t('selectDate')"
                format="DD/MM/YYYY"
                value-format="YYYY-MM-DDTHH:mm:ssZ"
              />
            </div>
          </template>
          <!-- Normal mode: cashier and sales rep -->
          <template v-else>
          <div class="mt-2 cashier-info flex justify-between">
            <h1>{{ t('user') }}</h1>
            <h1>{{ ctx.currentUser.fullName }}</h1>
          </div>
          <div class="grid grid-cols-5 justify-between mt-2">
            <h1 class="col-span-2">{{ t('salesRep') }}</h1>
            <el-select
              filterable
              clearable
              class="col-span-3"
              value-key="id"
              v-model="order.invoice.salesRepID"
              placeholder=""
              :remote-method="searchReps"
            >
              <el-option
                v-for="option in reps"
                :key="option.id"
                :label="option.fullName"
                :value="option.id"
              ></el-option>
            </el-select>
          </div>
          </template>

          <el-divider />

          <div class="customer-info p-4 bg-light-blue flex justify-between items-center">
            <div class="flex flex-col">
              <div class="text-primary flex items-center gap-1">
                <div v-if="customer?.isStarred" class="text-yellow-500 text-sm mb-1">
                  <Icon icon="material-symbols:star" width="24" height="24" />
                </div>
                <div class="font-bold">
                  {{ customerName === '' ? t('noCustomer') : customerName }}
                </div>
                <font-awesome-icon
                  v-if="customer?.id"
                  icon="pen"
                  class="text-primary cursor-pointer text-sm ml-1"
                  @click="openEditCustomerDialog"
                />
              </div>
              <small>{{ order.invoice.customer?.businessName }}</small>
              <small v-if="customerBalance > 0" class="text-dark-blue font-semibold mt-1">
                {{ t('balanceV2') }} {{ formatMoney(customerBalance, 'IQD') }}
              </small>
              <small v-if="customerCredit > 0" class="text-green-600 font-semibold mt-1">
                {{ t('creditV2') }} {{ formatMoney(customerCredit, 'IQD') }}
              </small>
            </div>
            <font-awesome-icon
              v-if="customerName"
              icon="times"
              class="text-primary cursor-pointer"
              @click="clearCustomer"
            />
          </div>

          <div class="customer-button w-full self-end mt-1">
            <horizontal-button
              :title="t('buttons.addCustomer')"
              faIcon="plus"
              styles="border border-primary text-primary button-secondary"
              @click="openCustomerDialog"
              :rounded="false"
            />
          </div>

          <el-divider />

          <div v-if="order.customerID && !isQuote" class="items-total flex justify-between items-center">
            <h1>{{ t('pouchID') }}</h1>
            <el-input
              id="pouchID"
              style="width: 150px"
              v-model="order.invoice.pouchID"
              show-word-limit
              clearable
            >
            </el-input>
          </div>

          <div class="items-total flex justify-between mt-1">
            <h1>{{ t('totalItems') }}</h1>
            <h1 v-if="order.orderItems.length">{{ getTotalItemsCount() }}</h1>
          </div>

          <el-divider />

          <div class="total-info">
            <div class="sub-total flex justify-between mb-2">
              <h1 class="text-l">{{ t('subTotal') }}</h1>
              <h1 v-if="order.orderItems.length" class="text-l">
                {{ formatDinero(order.computeTotal()) }}
              </h1>
            </div>
            <div class="total-discount flex justify-between">
              <div class="flex item-center justify-between">
                <h1 class="mr-2 text-l">{{ t('totalDiscount') }}</h1>
                <span v-if="order.orderItems.length" @click="openDiscountDialog">
                  <img src="../../assets/discountTag.svg" alt="discount icon" />
                </span>
              </div>
              <h1
                v-if="order.orderItems.length"
                class="text-red-400 text-l"
                :key="order.totalDiscountAmount"
              >
                - {{ formatDinero(order.totalDiscountDinero) }}
              </h1>
            </div>
            <div class="item-discounts flex justify-between mb-2">
              <h1 class="text-l"></h1>
              <h1 v-if="order.orderItems.length && getTotalItemDiscounts().getAmount() > 0" class="text-red-400 text-l">
               - {{ formatDinero(getTotalItemDiscounts()) }}
              </h1>
            </div>
            <template v-if="showCostAndProfit && order.orderItems.length">
              <div class="flex justify-between mb-1">
                <span class="text-xs text-gray-500">{{ t('totalCost') }}</span>
                <span class="text-xs text-gray-500">{{ formatDinero(getTotalCost()) }}</span>
              </div>
              <div class="flex justify-between mb-2">
                <span class="text-xs text-gray-500">{{ t('profitMargin') }}</span>
                <span class="text-xs font-medium" :class="getProfitMargin() >= 0 ? 'text-green-600' : 'text-red-500'">
                  {{ getProfitMargin().toFixed(1) }}%
                </span>
              </div>
            </template>
            <div v-if="0" class="flex justify-between">
              <h1 class="me-2 flex items-center gap-2">
                {{ t('shipping') }}
                <font-awesome-icon icon="truck" />
              </h1>
              <div class="flex items-center gap-2">
                <button
                  class="p-1 px-2 rounded-sm border border-primary bg-background w-[100px]"
                  :class="{ 'bg-cyan-500 text-white': order.invoice.shippingFeeOnUs }"
                  @click="
                    () => {
                      order.invoice.shippingFeeOnUs = !order.invoice.shippingFeeOnUs
                    }
                  "
                >
                  {{ t('onUs') }}
                </button>
                <money-input :currency="$ctx.currency" v-model="order.invoice.shippingFeeAmount" />
              </div>
            </div>
          </div>

          <el-divider />
          <div v-if="isEdit" class="w-full">
            <div class="flex justify-between mb-2">
              <h1 class="text-l">{{ t('total') }}</h1>
              <h1 v-if="order.orderItems.length" class="text-l bg-yellow-100">
                {{
                  formatMoney(
                    order.computeTotalAfterDiscount().getAmount() +
                      order.invoice.insuranceTotalDueAmount,
                    'IQD'
                  )
                }}
              </h1>
            </div>
            <div class="flex justify-between mb-2">
              <h1 class="text-l">{{ t('insurancePaid') }}</h1>
              <h1 class="text-l flex items-center gap-2">
                <div class="font-bold">
                  {{ formatMoney(order.invoice.insuranceTotalDueAmount, 'IQD') }}
                </div>
                <insurance-dialog :order="order" @insurance="onInsurance" />
              </h1>
            </div>
            <div class="flex justify-between mb-2">
              <h1 class="text-l">{{ t('customerPaid') }}</h1>
              <h1 class="text-l">
                {{ formatMoney(oldOrderPaidAmount, 'IQD') }}
              </h1>
            </div>
            <div class="flex justify-between mb-2">
              <h1 class="text-l">{{ t('paymentType') }}</h1>
              <h1 v-if="localData?.orderItems.length" class="text-l">
                <span v-if="localData?.paymentType == 'direct'">{{ t('invoiceTypes.full') }}</span>
                <span v-if="localData?.paymentType == 'credit_sale'">{{
                  t('invoiceTypes.credit')
                }}</span>
                <span v-if="localData?.paymentType == 'layaway'">{{
                  t('invoiceTypes.layaway')
                }}</span>
                <span v-if="localData?.paymentType == 'cash_on_delivery'">{{
                  t('invoiceTypes.cashOnDelivery')
                }}</span>
              </h1>
            </div>
            <div class="flex justify-between mb-2">
              <h1 class="text-l">{{ t('balance') }}</h1>
              <h1
                v-if="order.orderItems.length"
                class="text-l"
                :class="{ 'text-red-500': editBalance < 0, 'text-green-600': editBalance > 0 }"
              >
                {{ formatMoney(editBalance, 'IQD') }}
              </h1>
            </div>
          </div>
          <div
            v-else
            class="total flex flex-col justify-between items-center bg-yellow-100 p-2 -mx-1"
          >
            <h1 class="font-bold text-l">
              {{ t('total') }}
            </h1>
            <h1
              v-if="order.orderItems.length"
              class="font-bold 2xl:text-3xl lg:text-2xl text-l mt-1"
            >
              {{
                formatMoney(
                  order.computeTotalAfterDiscount().getAmount() +
                    order.invoice.insuranceTotalDueAmount,
                  'IQD'
                )
              }}
            </h1>
          </div>

          <div class="action-buttons">
            <!-- Quote mode: Save Quote button only -->
            <template v-if="isQuote">
              <horizontal-button
                v-if="order.orderItems.length"
                :title="t('saveQuote')"
                styles="button-primary 2xl:h-20 lg:h-16 h-12 2xl:text-3xl lg:text-2xl text-l font-bold mt-4"
                :rounded="false"
                @click="submitQuote"
              />
            </template>
            <!-- Normal checkout mode -->
            <template v-else>
            <horizontal-button
              v-if="order.orderItems.length"
              class="my-4"
              :title="isEdit ? t('buttons.cancelEdit') : t('buttons.cancel')"
              faIcon="cancel"
              styles="border border-red-500 h-12 text-l text-red-500 hover:bg-red-500 hover:text-white"
              @click="handleCancelOrder"
              :rounded="false"
            />
            <horizontal-button
              v-if="!isEdit"
              :title="t('buttons.checkout')"
              styles="button-primary 2xl:h-20 lg:h-16 h-12 2xl:text-3xl lg:text-2xl text-l font-bold mt-4"
              :rounded="false"
              @click="
                () => {
                  if (order.orderItems.length) {
                    checkoutDrawerOpen = true
                  }
                }
              "
            />
            <horizontal-button
              v-if="isEdit && editBalance == 0"
              :title="t('buttons.save')"
              :styles="'button-primary 2xl:h-20 lg:h-16 h-12 2xl:text-3xl lg:text-2xl text-l font-bold mt-4'"
              :rounded="false"
              @click="openCashierPinDialog(getCasherInputRef)"
            />
            <horizontal-button
              v-if="isEdit && editBalance != 0"
              :title="editBalance > 0 ? t('buttons.collect') : t('buttons.refund')"
              :styles="
                'button-primary 2xl:h-20 lg:h-16 h-12 2xl:text-3xl lg:text-2xl text-l font-bold mt-4' +
                (editBalance > 0 ? '' : ' !bg-red-600')
              "
              :rounded="false"
              @click="
                () => {
                  if (isEdit) {
                    if (editBalance < 0) {
                      refundDrawerOpen = true
                      return
                    }
                  }

                  if (order.orderItems.length) {
                    checkoutDrawerOpen = true
                  }
                }
              "
            />
            </template>
          </div>
          <el-drawer
            v-model="checkoutDrawerOpen"
            v-if="checkoutDrawerOpen && !isQuote"
            :direction="'rtl'"
            @open="openCheckoutDialog"
            :lock-scroll="false"
            class="overflow-scroll"
          >
            <div class="px-5" style="margin-top: -20px !important">
              <div class="mb-2 w-full">
                <!-- I chose to keep the below as a comment just in case we decided to use labels later on again -->
                <!-- <p class="mb-2 font-semibold">{{ t("selectPaymentType") }}</p> -->
                <el-radio-group class="w-full flex !flex-nowrap" v-model="order.paymentType">
                  <el-radio-button :disabled="isEdit" class="flex-1 text-2xl" label="direct" border>
                    {{ t('invoiceTypes.full') }}
                  </el-radio-button>
                  <el-radio-button
                    :disabled="
                      isEdit &&
                      !(
                        order.paymentType == 'cash_on_delivery' ||
                        order.paymentType == 'credit_sale'
                      )
                    "
                    class="flex-1 text-2xl"
                    label="credit_sale"
                    border
                  >
                    {{ t('invoiceTypes.credit') }}
                  </el-radio-button>
                  <el-radio-button
                    :disabled="isEdit"
                    class="flex-1 text-2xl"
                    label="layaway"
                    border
                  >
                    {{ t('invoiceTypes.layaway') }}
                  </el-radio-button>
                  <el-radio-button
                    :disabled="
                      isEdit &&
                      !(
                        order.paymentType == 'cash_on_delivery' ||
                        order.paymentType == 'credit_sale'
                      )
                    "
                    v-if="order.orderType != 'direct'"
                    class="flex-1 text-2xl"
                    label="cash_on_delivery"
                    border
                  >
                    {{ t('invoiceTypes.cashOnDelivery') }}
                  </el-radio-button>
                </el-radio-group>
              </div>

              <div class="mt-3 mb-2 flex items-center justify-between gap-3" :dir="$ctx.getDir()">
                <div class="font-semibold shrink-0">{{ t('dueOn') }}</div>
                <el-date-picker
                  style="width: 220px"
                  v-model="order.invoice.billDueDate"
                  type="date"
                  :placeholder="t('selectDate')"
                  format="DD/MM/YYYY"
                  value-format="YYYY-MM-DDTHH:mm:ssZ"
                  :disabled="dueOnDisabled"
                  :shortcuts="dueOnShortcuts"
                  :popper-class="$ctx.getDir() === 'rtl' ? 'rtl' : ''"
                  clearable
                />
              </div>

              <!--              <div>-->
              <!--                totalPaidAmount-->
              <!--                {{order.totalPaidAmount}}-->
              <!--              </div>-->
              <!--              <div>-->
              <!--                invoice totalPaidAmount-->
              <!--                {{order.invoice.totalPaidAmount}}-->
              <!--              </div>-->
              <multi-currency-payment-input
                v-if="!isEdit"
                v-model:total-paid="order.invoice.totalPaidAmount"
                v-model:change-due="order.changeDueAmount"
                v-model:total-paid-in-cash-before-change="order.invoice.reqOnlyTotalCashPaidAmount"
                v-model:total-paid-in-credit="order.invoice.reqOnlyTotalCreditPaidAmount"
                v-model:total-paid-in-epayment="order.invoice.reqOnlyTotalEpaymentAmount"
                v-model:cash-multi-currency-breakdown="
                  order.invoice.reqOnlyCashMultiCurrencyBreakdown
                "
                :totalDue="order.computeTotalAfterDiscount().getAmount()"
                :order="order"
                :customerID="order.customerID"
                :available-terminals="availableTerminals"
                :terminal-error="terminalError"
                @update:terminalID="(val) => { order.invoice.reqOnlyTerminalID = val; terminalError = false }"
                @update:lastFourDigits="(val) => { order.invoice.reqOnlyLastFourDigits = val }"
                @insurance="onInsurance"
              />
              <multi-currency-payment-input
                v-else
                v-model:total-paid="order.invoice.totalPaidAmount"
                v-model:change-due="order.changeDueAmount"
                v-model:total-paid-in-cash-before-change="order.invoice.reqOnlyTotalCashPaidAmount"
                v-model:total-paid-in-credit="order.invoice.reqOnlyTotalCreditPaidAmount"
                v-model:total-paid-in-epayment="order.invoice.reqOnlyTotalEpaymentAmount"
                v-model:cash-multi-currency-breakdown="
                  order.invoice.reqOnlyCashMultiCurrencyBreakdown
                "
                :totalDue="editBalance"
                :order="order"
                :available-terminals="availableTerminals"
                :terminal-error="terminalError"
                @update:terminalID="(val) => { order.invoice.reqOnlyTerminalID = val; terminalError = false }"
                @update:lastFourDigits="(val) => { order.invoice.reqOnlyLastFourDigits = val }"
                @insurance="onInsurance"
              />
              <!--              <div-->
              <!--                  class="flex justify-between items-center text-base font-bold mx-5 mt-1"-->
              <!--                  dir="ltr">-->
              <!--                <div>{{ t('shippingExpense') }}</div>-->
              <!--                <div dir="ltr" class="bg-white w-1/2 p-1 border border-gray-300">{{formatMoney(order.invoice.shippingFeeAmount || 0, "IQD")}}</div>-->
              <!--              </div>-->

              <div class="mt-1">
                <div>{{ t('notes') }}</div>
                <div>
                  <el-input v-model="order.paymentNotes" :rows="2" type="textarea" placeholder="" />
                </div>
              </div>

              <div class="flex items-center justify-between my-2">
                <horizontal-button
                  class="text-lg complete-payment"
                  :title="t('buttons.completePay')"
                  @click="openCashierPinDialog(getCasherInputRef)"
                  :rounded="false"
                />
              </div>
            </div>
          </el-drawer>
          <el-drawer
            v-model="refundDrawerOpen"
            v-if="refundDrawerOpen && !isQuote"
            :direction="'rtl'"
            @open="openCheckoutDialog"
            :lock-scroll="false"
            class="overflow-scroll"
          >
            <div class="px-5" style="margin-top: -20px !important">
              <div class="mt-5 grid items-center grid-cols-3">
                <div class="col-span-1">{{ t('totalRefund') }}</div>
                <div class="p-1 col-span-2 bg-yellow-100">
                  {{ formatMoney(Math.abs(editBalance), 'IQD') }}
                </div>
              </div>

              <!-- Cash Refund Section -->
              <div class="mt-5">
                <div class="mb-2 font-semibold text-lg">{{ t('refundAccountTypes.cash') }}</div>

                <div class="flex flex-col">
                  <div class="flex justify-between items-center">
                    <h1 class="text-l">{{ '(a)  ' + t('cashRefundedInIQD') }}</h1>
                    <div class="line border w-1/12 mx-2"></div>
                    <h1 class="text-l">
                      {{ formatMoney(order.requestOnlyRefundAmountInIQD, 'IQD') }}
                    </h1>
                  </div>
                  <div>
                    <money-input
                      v-model="order.requestOnlyRefundAmountInIQD"
                      currency="IQD"
                      ref="refundAmountInputIQD"
                    />
                  </div>
                </div>

                <div class="mt-5 flex flex-col">
                  <div class="flex justify-between items-center">
                    <h1 class="text-l">{{ '(b)  ' + t('ePaymentRefundedInIQD') }}</h1>
                    <div class="line border w-1/12 mx-2"></div>
                    <h1 class="text-l">
                      {{ formatMoney(order.requestOnlyRefundAmountInEpayment, 'IQD') }}
                    </h1>
                  </div>
                  <div>
                    <money-input
                      v-model="order.requestOnlyRefundAmountInEpayment"
                      currency="IQD"
                      ref="refundAmountInputEpayment"
                    />
                  </div>
                </div>

                <div class="mt-5 flex flex-col">
                  <div class="flex justify-between items-center">
                    <h1 class="text-l">{{ '(c)  ' + t('cashRefundedInUSD') }}</h1>
                    <div class="line border w-1/12 mx-2"></div>
                    <h1 class="text-l">
                      {{ formatMoney(order.requestOnlyRefundAmountInUSD, 'USD') }}
                    </h1>
                  </div>
                  <div>
                    <new-money-input
                      v-model="order.requestOnlyRefundAmountInUSD"
                      currency="USD"
                      ref="refundAmountInputUSD"
                    />
                  </div>
                </div>
              </div>

              <!-- Credit Refund Section -->
              <div class="mt-5" v-if="order.customerID">
                <div class="mb-2 font-semibold text-lg">{{ t('refundAccountTypes.credit') }}</div>

                <div class="flex flex-col">
                  <div class="flex justify-between items-center">
                    <h1 class="text-l">{{ '(d)  ' + t('creditRefundedInIQD') }}</h1>
                    <div class="line border w-1/12 mx-2"></div>
                    <h1 class="text-l">
                      {{ formatMoney(order.requestOnlyRefundAmountInCredit || 0, 'IQD') }}
                    </h1>
                  </div>
                  <div>
                    <money-input
                      v-model="order.requestOnlyRefundAmountInCredit"
                      currency="IQD"
                      ref="refundAmountInputCredit"
                    />
                  </div>
                </div>
              </div>

              <!-- Calculation breakdown -->
              <div class="mt-5 flex flex-col text-sm">
                <div>(a) {{ formatMoney(order.requestOnlyRefundAmountInIQD, 'IQD') }}</div>
                <div>+ (b) {{ formatMoney(order.requestOnlyRefundAmountInEpayment, 'IQD') }}</div>
                <div>
                  + (c)
                  {{ formatMoney(order.requestOnlyRefundAmountInUSD, 'USD') }}
                  x
                  {{ formatMoney($ctx.exchangeRates.IQD, 'IQD') }}
                  =
                  {{ formatMoney((order.requestOnlyRefundAmountInUSD / 100) * $ctx.exchangeRates.IQD, 'IQD') }}
                </div>
                <div v-if="order.customerID">+ (d) {{ formatMoney(order.requestOnlyRefundAmountInCredit || 0, 'IQD') }}</div>
              </div>

              <div class="mt-5 grid items-center grid-cols-3">
                <div class="col-span-1">{{ t('totalRefunded') }}</div>
                <div class="p-1 col-span-2 bg-green-100">
                  {{
                    formatMoney(
                      order.requestOnlyRefundAmountInIQD +
                        order.requestOnlyRefundAmountInEpayment +
                        (order.requestOnlyRefundAmountInUSD / 100) * $ctx.exchangeRates.IQD +
                        (order.requestOnlyRefundAmountInCredit || 0),
                      'IQD'
                    )
                  }}
                </div>
              </div>

              <div class="mt-5">
                <div>{{ t('notes') }}</div>
                <div>
                  <el-input v-model="order.paymentNotes" :rows="2" type="textarea" placeholder="" />
                </div>
              </div>

              <div class="flex items-center justify-between my-2">
                <horizontal-button
                  class="text-lg complete-payment"
                  :title="t('buttons.completePay')"
                  @click="openCashierPinDialog(getCasherInputRef)"
                  :rounded="false"
                />
              </div>
            </div>
          </el-drawer>
          <el-drawer
            v-if="!isQuote"
            v-model="quoteDrawerOpen"
            :direction="'rtl'"
            @open="openQuoteDialog"
            :lock-scroll="false"
            class="overflow-scroll"
          >
            <div class="p-5 h-full">
              <div class="flex p-6 justify-between bg-gray-300">
                <h1 class="text-l">quote #</h1>
                <h1 class="text-l">001929</h1>
              </div>
              <div class="flex justify-center items-center flex-col h-3/6">
                <img src="../../assets/quoteDone.svg" alt="quote done " />
                <h1 class="mt-4 text-2xl">quote have been saved</h1>
              </div>
              <div class="h-2/6 flex flex-col justify-between">
                <horizontal-button
                  styles="h-20 border-primary text-primary text-2xl"
                  :title="t('buttons.printQuote')"
                  @click="() => {}"
                  :rounded="false"
                />
                <horizontal-button
                  styles="h-20 border-primary text-primary text-2xl font-bold"
                  :title="t('buttons.checkoutQuote')"
                  @click="() => {}"
                  :rounded="false"
                />
                <horizontal-button
                  class="mt-4"
                  styles="h-20 bg-primary text-white text-2xl font-bold"
                  :title="t('buttons.newTransaction')"
                  @click="resetOrder"
                  :rounded="false"
                />
              </div>
            </div>
          </el-drawer>
          <el-dialog
            :title="t('selectCustomer')"
            v-model="customerDialogOpen"
            width="30%"
            @close="closeCustomerDialog"
          >
            <div class="flex items-center justify-center" :dir="$ctx.getDir()">
              <el-select
                ref="customerSelectRef"
                filterable
                clearable
                class="w-full"
                v-model="order.customerID"
                value-key="contactDisplayName"
                remote
                @change="onCustomerNameChange"
                :remote-method="searchCustomers"
                :key="order.customerID"
              >
                <el-option
                  v-for="option in customers"
                  :key="option.id"
                  :label="option.contactDisplayName"
                  :value="option.id"
                >
                  <span style="float: left">{{ option.contactDisplayName }}</span>
                  <span
                    v-if="option.phone"
                    style="
                      float: right;
                      color: var(--el-text-color-secondary);
                      font-size: 13px;
                    "
                  >
                    {{ option.phone }}
                  </span>
                </el-option>
                <template #footer>
                  <button v-if="customerSearchTerm" type="button" class="add-typed-customer" :dir="$ctx.getDir()" @click="openNewCustomer">
                    <span class="fa fa-plus"></span>
                    <bdi>{{ t('addAsNewCustomer', { name: customerSearchTerm }) }}</bdi>
                  </button>
                </template>
              </el-select>
            </div>
            <template #footer>
              <span class="dialog-footer">
                <el-button
                  @click="openNewCustomer"
                  type="secondary"
                  ><span class="fa fa-plus"></span>
                  {{ t('views.customers.newCustomer') }}</el-button
                >
                <el-button type="primary" @click="closeCustomerDialog">{{
                  t('buttons.confirm')
                }}</el-button>
              </span>
            </template>
          </el-dialog>
          <el-dialog
            :title="t('selectSerials')"
            v-model="insertSerialDialogVisible"
            width="30%"
            @close="closeSerialDialog"
          >
            <div class="flex items-center justify-center" :dir="$ctx.getDir()">
              <el-select
                v-if="currentOrderItem.item.id"
                filterable
                clearable
                multiple
                class="w-full"
                v-model="currentOrderItem.serialIDs"
                :multiple-limit="currentOrderItem.quantity"
              >
                <el-option
                  v-for="(item, i) in getOrderItemSerials(currentOrderItem)"
                  :key="i"
                  :label="`${item.number} ${item?.used ? t('used') : ''}`"
                  :value="item.id"
                  ref="serialInput"
                >
                  <span :class="{ 'float-right': $ctx.getDir() == 'rtl' }">
                    {{ item.number }} {{ item?.used ? t('used') : '' }}
                  </span>
                </el-option>
              </el-select>
            </div>
            <template #footer>
              <span class="dialog-footer">
                <el-button type="primary" @click="closeSerialDialog">{{
                  t('buttons.confirm')
                }}</el-button>
              </span>
            </template>
          </el-dialog>
          <el-dialog v-model="discountDialogOpen" width="30%" @close="closeDiscountDialog">
            <div class="w-full flex flex-col items-center justify-center" :dir="$ctx.getDir()">
              <div class="w-full flex items-center justify-between">
                <h1 class="text-l font-bold">{{ t('discountSubTotal') }}</h1>
                <h1 class="text-l font-bold">
                  {{ formatDinero(order.computeTotal()) }}
                </h1>
              </div>

              <el-divider />

              <el-checkbox
                v-if="canOverrideDiscount"
                v-model="order.applyDiscountToAll"
                class="self-start mb-3"
              >
                <span class="text-red-500 font-semibold">{{ t('applyDiscountOnNonDiscountable') }}</span>
              </el-checkbox>

              <el-radio-group class="w-full flex mb-4" v-model="order.discountType">
                <el-radio-button class="flex-1" label="percentage" border>
                  {{ t('discountPercentage') }}
                </el-radio-button>
                <el-radio-button class="flex-1" label="amount" border>
                  {{ t('discountAmount') }}
                </el-radio-button>
              </el-radio-group>

              <div
                v-if="order.discountType === 'percentage'"
                class="mb-4 w-full flex items-center justify-between"
              >
                <h1 class="text-l font-bold">{{ t('discountPercentage') }}</h1>
                <div class="flex items-center">
                  <span class="mx-6 text-l font-bold">%</span>
                  <el-input-number
                    v-model="order.discountByPercentage"
                    :step="1"
                    size="medium"
                    :min="0"
                    :controls="false"
                    ref="discountPercentageInput"
                  />
                </div>
              </div>
              <div
                v-if="order.discountType === 'amount'"
                class="mb-6 w-full flex items-center justify-between"
              >
                <h1 class="text-l font-bold">{{ t('discountAmount') }}</h1>
                <div class="flex items-center">
                  <money-input
                    class="mx-8"
                    :currency="$ctx.currency"
                    v-model="order.discountByAmount"
                    ref="discountAmountInput"
                  />
                  <!-- <el-input-number
                    v-model="order.discountByAmount"
                    size="medium"
                    :min="0"
                    :controls="false"
                    ref="discountAmountInput"
                  /> -->
                </div>
              </div>

              <div class="w-full flex items-center justify-between bg-gray-200 p-4">
                <h1 class="text-l font-bold">{{ t('totalAfterDiscount') }}</h1>
                <h1 class="text-blue-500 text-l font-bold">
                  {{ formatDinero(order.computeTotalAfterDiscount()) }}
                </h1>
              </div>
            </div>
            <template #footer>
              <span class="dialog-footer">
                <el-button type="primary" class="bg-primary" @click="closeDiscountDialog">{{
                  t('buttons.confirm')
                }}</el-button>
                <el-button type="danger" @click="cancelDiscount">{{
                  t('buttons.cancelDiscount')
                }}</el-button>
              </span>
            </template>
          </el-dialog>
          <el-dialog v-model="discountCodeDialogOpen" width="30%" @close="closeDiscountCodeDialog">
            <div class="w-full flex flex-col" :dir="$ctx.getDir()">
              <p class="text-sm text-gray-600 mb-4">{{ t('discountCodePrompt') }}</p>
              <div class="flex items-center gap-3">
                <label class="whitespace-nowrap text-sm font-semibold text-gray-700">{{ t('discountCode') }}</label>
                <el-input
                  v-model="discountCodeInput"
                  class="flex-1"
                  maxlength="11"
                  placeholder="000-000-000"
                  @keyup.enter="confirmDiscountCode"
                  ref="discountCodeInputRef"
                />
              </div>
              <p v-if="discountCodeError" class="text-red-500 text-sm mt-2">{{ discountCodeError }}</p>
            </div>
            <template #footer>
              <span class="dialog-footer">
                <el-button
                  type="primary"
                  class="bg-primary"
                  :loading="discountCodeChecking"
                  @click="confirmDiscountCode"
                  >{{ t('buttons.confirm') }}</el-button
                >
                <el-button type="danger" @click="closeDiscountCodeDialog">{{ t('discountCodeCancel') }}</el-button>
              </span>
            </template>
          </el-dialog>
          <el-dialog v-model="newCustomerDialogVisiable" :title="t('views.customers.newCustomer')">
            <NewCustomerDialog @success="onCustomerCreater" :initName="customerSearchTerm"></NewCustomerDialog>
          </el-dialog>
          <el-dialog v-model="editCustomerDialogVisiable" :title="t('views.customers.editCustomer')">
            <NewCustomerDialog
              v-if="customer && editCustomerDialogVisiable"
              @success="onCustomerCreater"
              v-model="customer"
            ></NewCustomerDialog>
          </el-dialog>
          <el-dialog
            v-model="cancelOrderDialogOpen"
            :title="t('confirmCancelTitle')"
            width="30%"
          >
            <div class="text-center">
              <p class="mb-4">{{ t('confirmCancelMessage') }}</p>
            </div>
            <template #footer>
              <span class="dialog-footer">
                <el-button @click="cancelOrderDialogOpen = false">
                  {{ t('buttons.no') }}
                </el-button>
                <el-button type="danger" @click="confirmCancelOrder">
                  {{ t('buttons.yes') }}
                </el-button>
              </span>
            </template>
          </el-dialog>
          <el-dialog v-if="!isQuote" v-model="cashierPinDialogOpen" width="30%">
            <div>
              <!-- Enter the pin -->
              <div class="my-2">
                <p>{{ t('enterCashierPin') }}</p>
              </div>

              <!-- password input up to 6 chars -->
              <div class="my-2">
                <el-input
                  id="password"
                  v-model="cashierPin"
                  type="password"
                  required
                  show-password
                  autofocus
                  dir="ltr"
                  :maxlength="4"
                  :minlength="4"
                  @keyup.enter="submitOrder"
                  ref="cashierPinInput"
                />
              </div>

              <!-- finish payment -->
              <div class="flex items-center justify-between mb-2">
                <horizontal-button
                  class="text-lg complete-payment"
                  :title="t('buttons.completePay')"
                  @click="submitOrder"
                  :rounded="false"
                />
              </div>
            </div>
          </el-dialog>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, nextTick, ref, watch, onMounted, computed } from 'vue'
import HorizontalButton from '@/components/HorizontalButton.vue'
import Item from '@/models/inventory/Item'
import Stock from '@/models/inventory/Stock'
import { i18nOrderMessages, Order, OrderItem } from '@/models/sales/Order'
import PaymentInput from './components/PaymentInput.vue'
import { useHTTP } from '@/plugins/http'
import { useAlertModal } from '@/plugins/alert-modal/alert-modal'
import { useI18n } from 'vue-i18n'
import { localizeFieldName } from '@/plugins/i18n'
import { Register, RegisterLog } from '@/models/company/Register'
import { useContext } from '@/plugins/context'
import { can } from '@/plugins/access-control'
import { useRouter, useRoute } from 'vue-router'
import AlertBadge from '@/components/AlertBadge.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import Customer from '@/models/customer/Customer'
import Dinero, { Currency } from 'dinero.js'
import { formatDinero, formatMoney } from '@/utils/money'
import { Route } from '@/router/routes'
import User from '@/models/user/User'
import Location from '@/models/company/Location'
import MoneyInput from '@/components/form/MoneyInput.vue'
import NewCustomerDialog from '@/dialogs/customer/NewCustomerDialog.vue'
import { ElInput, ElMessage } from 'element-plus'
import { Search } from '@element-plus/icons-vue'
import MultiCurrencyPaymentInput from '@/components/money/MultiCurrencyPaymentInput.vue'
import { nilUUID } from '@/models/UUIDBase'
import QuantityInput from '@/views/inventory/items/QuantityInput.vue'
import ItemPack from '@/models/inventory/ItemPack'
import { useSettingsStore } from '@/stores/settingsStore'
import CustomPriceInput from '@/views/inventory/items/CustomPriceInput.vue'
import successbeep from '@/assets/sounds/success.mp3'
import errorbeep from '@/assets/sounds/error.mp3'
import SpecialOrderItemDialog from '@/views/sales/SpecialOrderItemDialog.vue'
import Delivery from '@/models/delivery/Delivery'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { Icon } from '@iconify/vue'
import BarcodeScanner from './BarcodeScanner.vue'
import NewMoneyInput from '@/components/form/NewMoneyInput.vue'
import InsuranceDialog from './InsuranceDialog.vue'
import ItemDiscountDialog from './components/ItemDiscountDialog.vue'
import { isOrderDueOnDisabled, orderDueOnShortcutDates } from '@/utils/orderDueOn'

export default defineComponent({
  name: 'new-checkout',
  components: {
    Icon,
    FontAwesomeIcon,
    SpecialOrderItemDialog,
    CustomPriceInput,
    QuantityInput,
    MultiCurrencyPaymentInput,
    HorizontalButton,
    PaymentInput,
    AlertBadge,
    LoadingSpinner,
    MoneyInput,
    NewCustomerDialog,
    ElInput,
    BarcodeScanner,
    NewMoneyInput,
    InsuranceDialog,
    ItemDiscountDialog
  },
  async mounted() {
    // load register status
    try {
      this.loading = true

      if (!this.isQuote) {
        this.searchReps()
        const registerLog = await this.http.get<RegisterLog>(
          `${Register.ENDPOINT}/last-status/${this.ctx.currentRegister.id}`
        )

        this.ctx.updateRegisterStatus(RegisterLog.from(registerLog).action)

        if (!registerLog.action || registerLog.action === 'close') {
          await this.router.push(this.$Route.SALES_REGISTERS_OPEN_REGISTER)
        }

        const top8Items = await this.$http.get<Item[]>('/inventory/items/top8items')
        this.top8Items = top8Items.map((item) => Item.from(item))

        const deliveriesResp = await this.$http.get<Delivery[]>(Delivery.ENDPOINT)
        this.deliveries = deliveriesResp.map((delivery) => Delivery.from(delivery))
      }

      if (this.$route.query.customer_id) {
        const selectedCustomer = Customer.from(
          await this.$http.get<Customer>(`customer/customers/${this.route.query.customer_id}`)
        )
        this.customers = [selectedCustomer]
        this.order.customerID = selectedCustomer.id
        this.selectedIndex = 0
        this.customer = selectedCustomer
        this.customerName = selectedCustomer.contactDisplayName
      }

      // Pre-fill from quote if quoteId is provided
      if (this.$route.query.quoteId) {
        const quoteId = this.$route.query.quoteId as string
        const quote = Order.from(await this.$http.get<Order>(`/sales/quotes/${quoteId}`))
        this.order = quote
        this.order.orderType = 'direct'
        this.order.computeTotal()

        // Restore total discount from quote's invoice so it's reflected during checkout
        if (quote.invoice?.totalDiscountAmount) {
          this.order.discountType = 'amount'
          this.order.discountByAmount = quote.invoice.totalDiscountAmount
        }

        // Restore serialIDs from saved orderItemSerials so they show as selected during checkout
        for (const oi of this.order.orderItems) {
          if (oi.item.isSerialized && oi.orderItemSerials?.length) {
            oi.serialIDs = oi.orderItemSerials.map((s) => s.itemSerial?.id || s.itemSerialID)
          }
        }

        this.order.requestOnlyQuoteID = quoteId

        if (quote.invoice?.customer) {
          this.customers = [quote.invoice.customer]
          this.customer = quote.invoice.customer
          this.customerName = quote.invoice.customer.contactDisplayName
        }

        if (quote.invoice?.paymentType) {
          this.paymentType = quote.invoice.paymentType
        }
      }
    } catch (error) {
      this.error.title = error.title
      this.error.body = error.body
    } finally {
      this.loading = false
      setTimeout(() => {
        this.searchInput.focus()
      }, 100)
    }
  },

  watch: {
    'order.totalDiscountAmount': {
      handler: function (value: number) {
        if (isNaN(value)) {
          value = 0
        }
        this.order.totalDiscountDinero = Dinero({
          amount: value,
          currency: this.order.currency,
          precision: this.order.precision
        })
      }
    },
    customer: function (val: Customer | null) {
      // if (val) {
      //   this.updateOrderItemsCustomPrice(val)
      // } else {
      //   for (let i = 0; i < this.order.orderItems.length; i++) {
      //     // this.onCustomPriceInfo(null, this.order.orderItems[i])
      //     // this.order.orderItems[i].itemSalePriceAmount =
      //     //   this.order.orderItems[i].item.salePriceAmount!
      //   }
      // }
    }
  },

  emits: ['orderChanged', 'orderSold', 'selectCustomer', 'hasChanges'],

  props: ['localData', 'isEdit', 'isQuote', 'isActiveTab'],

  setup(props, vueCtx) {
    const http = useHTTP()
    const alertModal = useAlertModal()
    const ctx = useContext()
    const router = useRouter()
    const route = useRoute()
    const selectedIndex = ref()
    const itemSearchTerm = ref('')
    const showCostAndProfit = ref(false)
    const currentItemPack = ref<ItemPack | null>()
    const appendToOrderItems = ref(false)
    const serialSearchTerm = ref('')
    const order = ref(new Order())
    const oldOrderPaidAmount = ref(0)
    const paymentType = ref('')
    const checkoutDrawerOpen = ref(false)
    const refundDrawerOpen = ref(false)
    const quoteDrawerOpen = ref(false)
    const pressedEnter = ref(false)
    const searchLoading = ref(false)
    const data = ref(order.value.orderItems)
    const error = ref({ title: '', body: '' })
    const stockQuantityErrors = ref(new Map())
    const originalOrderQuantities = ref(new Map<string, number>())
    const loading = ref(false)
    const customerDialogOpen = ref(false)
    const newCustomerDialogVisiable = ref(false)
    const customerSearchTerm = ref('')
    const customerSelectRef = ref<{ blur?: () => void } | null>(null)
    const editCustomerDialogVisiable = ref(false)
    const customers = ref(new Array<Customer>())
    const customer = ref(null as Customer | null)
    const customerName = ref('')
    const customerBalance = ref(0)
    const customerCredit = ref(0)
    const direction = ref('rtl')
    const currentOrderItem = ref(new OrderItem(new Item()))
    const insertSerialDialogVisible = ref(false)
    const discountDialogOpen = ref(false)
    // Discount-code popup: shown when a user without the "apply discount" permission
    // tries to open the discount dialog. A valid code unlocks it for this order.
    const discountCodeDialogOpen = ref(false)
    const discountCodeInput = ref('')
    const discountCodeError = ref('')
    const discountCodeChecking = ref(false)
    const discountUnlocked = ref(false)
    // Shows the "apply discount on non-discountable items" checkbox in the discount dialog.
    const canOverrideDiscount = computed(() => can('update', '/sales/applydiscount'))
    // Controls whether the per-item discount button is shown. Only users with the
    // "apply discount" permission can give individual discounts — the discount-code
    // unlock does NOT enable per-item discounts.
    const canApplyDiscount = computed(() => can('create', '/sales/applydiscount'))
    const discountCodeInputRef = ref()
    const cashierPinDialogOpen = ref(false)
    const cashierPin = ref('')
    const isSubmitting = ref(false)
    const cancelOrderDialogOpen = ref(false)
    // const cashierPinInput = ref<HTMLInputElement | null>(null);
    const searchInput = ref()
    const discountPercentageInput = ref()
    const discountAmountInput = ref()
    const top8Items = ref([] as Array<Item>)
    const tempSearchResult = ref<Array<Item>>(null)
    const table = ref<any>()
    const deliveries = ref<Array<Delivery>>(null)
    const quoteValidUntil = ref<string | null>(null)
    const quotePaymentType = ref('direct')
    const availableTerminals = ref<any[]>([])
    const terminalError = ref(false)

    const editBalance = computed(() => {
      return (
        order.value.computeTotalAfterDiscount().getAmount() +
        order.value.invoice.insuranceTotalDueAmount -
        oldOrderPaidAmount.value -
        order.value.invoice.insuranceTotalDueAmount
      )
    })

    const dueOnDisabled = computed(() => isOrderDueOnDisabled(order.value.paymentType))

    const isOrderFulfillmentMode = computed(() => {
      return (
        useSettingsStore().getValue('order_fulfilling_module') == 'true' &&
        (order.value.orderType === 'delivery' || order.value.orderType === 'pick_up') &&
        (order.value.orderStatus === 'unprocessed' ||
          order.value.orderStatus === 'processing' ||
          !order.value.orderStatus)
      )
    })

    const orderTypes = ref([
      { label: 'orderTypes.direct', value: 'direct' },
      { label: 'orderTypes.delivery', value: 'delivery' },
      { label: 'orderTypes.pickup', value: 'pick_up' }
    ])

    const settingsStore = useSettingsStore()
    const { t } = useI18n({
      useScope: 'global',
      messages: {
        en: {
          ...i18nOrderMessages.en,
          shipping: 'Shipping',
          onUs: 'On Us',
          carrier: 'Carrier',
          shippingExpense: 'Shipping',
          notes: 'Notes',
          totalItems: 'Total Items',
          totalDiscount: 'Total Discount',
          subTotal: 'Sub Total',
          totalDue: 'Total Due',
          searchItems: 'Enter item barcode or SKU or name',
          delete: 'Delete',
          noCustomer: 'No Customer Selected',
          balance: 'Balance',
          balanceV2: 'Balance',
          creditV2: 'Credit',
          minimumHasToBePaid: 'Minimum has to be Paid',
          noSalesRep: 'No Sales Rep Selected',
          amount: 'Amount',
          percentage: 'Percentage',
          applyDiscountOnNonDiscountable: 'Apply discount on non-discountable items',
          discountSubTotal: 'Sub Total',
          discountPercentage: 'Percentage',
          discountAmount: 'Amount',
          totalAfterDiscount: 'Total',
          discountCode: 'Discount Code',
          discountCodePrompt: "You don't have permission to apply a discount. Enter a discount code to activate.",
          discountCodeInvalid: 'Invalid or already-used discount code.',
          discountCodeInvalidFormat: 'Enter a valid 9-digit code.',
          discountCodeCancel: 'Cancel',
          enterCashierPin: 'Enter cashier pin',
          confirmCancelTitle: 'Confirm Cancel',
          confirmCancelMessage: 'You have unsaved changes. Are you sure you want to cancel this order?',
          buttons: {
            addCustomer: 'Add Customer',
            payment: 'Payment',
            direct: 'Direct',
            creditSale: 'Credit Sale',
            layaway: 'Layaway',
            quote: 'Save as Quote',
            cancel: 'Cancel Order',
            cancelEdit: 'Cancel Edit',
            cancelDiscount: 'Cancel Discount',
            confirm: 'Confirm',
            completePay: 'Process Payment',
            checkout: 'Checkout',
            collect: 'Collect',
            refund: 'Refund',
            save: 'Save',
            printQuote: 'Print Quote',
            checkoutQuote: 'Checkout Quote Items',
            newTransaction: 'New Transaction',
            cashOnDelivery: 'Cash On Delivery',
            yes: 'Yes, Cancel',
            no: 'No, Keep Editing'
          },

          selectSerials: 'Select serials',
          selectCustomer: 'Select a customer',
          addAsNewCustomer: 'Add "{name}" as a new customer',
          selectPaymentType: 'Select order type',

          outOfStockErr:
            'Item {name} is out of stock. Please update your stock before selling this item',
          cantSellItemPackWithSerializedItem: "Can't sell item pack with Serialized Item",

          quantityExceedsTheStockQuantity:
            'There are only {quantity} unit available for sale for item {name} in the system. Please make sure to update the item stock before executing this order.',

          successMsg: 'Sale complete',

          errPaymentMustMatch:
            "Payment must be made in full. If you'd like to pay partially, choose credit sale",
          errTerminalRequired: 'A terminal must be selected when using E-Payment',

          errCashierPinRequired: 'Cashier pin is required',
          itemCustomPrice: 'Item Custom Price',
          selectedPrice: 'Selected Price',
          itemNotFound: 'Item Not Found',
          canotEditReturnedOrder: "Can't edit order that is partially or fully returned",
          refundAccountTypes: {
            cash: 'Cash',
            credit: 'Credit'
          },
          cashRefundedInIQD: 'Cash Refunded in IQD',
          ePaymentRefundedInIQD: 'E-Payment Refunded in IQD',
          cashRefundedInUSD: 'Cash Refunded in USD',
          creditRefundedInIQD: 'Credit Refunded in IQD',
          totalRefund: 'Total Refund',
          totalRefunded: 'Total Refunded',
          batchNumber: 'Batch Number',
          used: '(Used)',
          quotedBy: 'Quoted By',
          paymentTerms: 'Payment Terms',
          validUntil: 'Valid Until',
          selectDate: 'Select date',
          dueOnInOneWeek: '1 Week',
          dueOnInTwoWeeks: '2 Weeks',
          dueOnInOneMonth: '1 Month',
          dueOnInTwoMonths: '2 Months',
          dueOnInThreeMonths: '3 Months',
          dueOnInSixMonths: '6 Months',
          dueOnInOneYear: '1 Year',
          saveQuote: 'Save Quote',
          quoteCreated: 'Quote created successfully',
          noItems: 'Please add at least one item',
          direct: 'Direct',
          credit_sale: 'Credit Sale',
          layaway: 'Layaway',
          cash_on_delivery: 'Cash on Delivery',
          costAndProfit: 'Cost & Profit',
          cost: 'Cost',
          totalCost: 'Total Cost (IQD)',
          profitMargin: 'Profit Margin'
        },
        ar: {
          ...i18nOrderMessages.ar,
          shipping: 'التوصيل',
          onUs: 'علينا',
          carrier: 'شركة التوصيل',
          shippingExpense: 'اجور نقل',
          notes: 'الملاحظات',
          totalItems: 'إجمالي العناصر',
          totalDiscount: 'اجمالي التخفيض',
          subTotal: 'المبلغ الإجمالي',
          totalDue: 'الاجمالي المستحق',
          searchItems: 'ادخل الباركود او ال SKU او اسم المنتج',
          delete: 'حذف',
          noCustomer: 'لم يتم اختيار أي زبون',
          balance: 'الرصيد',
          balanceV2: 'الدين',
          creditV2: 'الرصيد',
          minimumHasToBePaid: 'يجب دفع الحد الأدنى',
          noSalesRep: 'لم يتم اختيار أي ممثل بيع',
          amount: 'الخصم بالمبلغ',
          percentage: 'الخصم بالنسبة',
          applyDiscountOnNonDiscountable: 'تطبيق التخفيض على المنتجات غير القابلة للتخفيض',
          discountSubTotal: 'المجموع',
          discountPercentage: 'نسبة مئوية',
          discountAmount: 'مبلغ',
          totalAfterDiscount: 'المجموع بعد التخفيض',
          discountCode: 'كود الخصم',
          discountCodePrompt: 'ليس لديك صلاحية لتطبيق الخصم. أدخل كود الخصم للتفعيل.',
          discountCodeInvalid: 'كود الخصم غير صالح أو تم استخدامه مسبقًا.',
          discountCodeInvalidFormat: 'أدخل كودًا صالحًا مكوّنًا من 9 أرقام.',
          discountCodeCancel: 'إلغاء',
          enterCashierPin: 'ادخل رمز الكاشير الخاص بك',
          confirmCancelTitle: 'تأكيد الإلغاء',
          confirmCancelMessage: 'لديك تغييرات غير محفوظة. هل أنت متأكد من إلغاء هذا الطلب؟',
          buttons: {
            addCustomer: 'اضافة زبون',
            payment: 'دفع',
            direct: 'مباشر',
            creditSale: 'آجل',
            layaway: 'عربون',
            quote: 'الحفظ كعرض',
            cancel: 'الغاء البيع',
            cancelEdit: 'الغاء التعديل',
            cancelDiscount: 'الغاء التخفيض',
            confirm: 'التأكيد',
            completePay: 'اتمام الدفع',
            checkout: 'الدفع',
            collect: 'جمع',
            refund: 'إسترجاع',
            save: 'حفظ',
            printQuote: 'اطبع قائمة التسعير',
            checkoutQuote: 'إتمام بيع قائمة التسعير',
            newTransaction: 'عملية بيع جديدة',
            cashOnDelivery: 'دفع عن التوصيل',
            yes: 'نعم، إلغاء',
            no: 'لا، متابعة التعديل'
          },

          selectSerials: 'اختر الارقام',
          selectCustomer: 'اختر زبون',
          addAsNewCustomer: 'إضافة "{name}" كزبون جديد',
          selectPaymentType: 'اختر نوع البيع',

          outOfStockErr: 'نفذ مخزون المنتج {name}. يرجى اعادة تحديث المخزن قبل بيع المنتج.',
          cantSellItemPackWithSerializedItem: 'لا يمكن بيع حزمة منتج تحتوي على منتج مرقم',
          quantityExceedsTheStockQuantity:
            'يحتوي المخزن على {quantity} قطعة من المنتج {name}. لأتمام البيع، يرجى تحديث كمية المنتج في المخزن.',

          successMsg: 'تم اكمال البيع',

          errPaymentMustMatch:
            'يجب ان يتم دفع المبلغ بشكل كامل. لدفع مبلغ جزئي، قم بتحويل نوع الدفع الى دين او عربون',
          errTerminalRequired: 'يجب اختيار جهاز عند استخدام الدفع الإلكتروني',

          errCashierPinRequired: 'يرجى ادخال رمز الكاشير لاتمام البيع',
          itemCustomPrice: 'اختيار سعر المنتج',
          selectedPrice: 'السعر المختار',
          itemNotFound: 'لم يتم ايجاد المنتج',
          canotEditReturnedOrder: 'لا يمكن تعديل طلب متسرجع جزئيا او كليا',
          refundAccountTypes: {
            cash: 'نقدي',
            credit: 'رصيد'
          },
          cashRefundedInIQD: 'الاسترجاع النقدي بالدينار العراقي',
          ePaymentRefundedInIQD: 'الاسترجاع الالكتروني بالدينار العراقي',
          cashRefundedInUSD: 'الاسترجاع النقدي بالدولار الامريكي',
          creditRefundedInIQD: 'الاسترجاع على الحساب بالدينار العراقي',
          totalRefund: 'اجمالي الاسترجاع',
          totalRefunded: 'اجمالي الاسترجاع',
          batchNumber: 'رقم الوجبة',
          used: '(مستخدم)',
          quotedBy: 'بواسطة',
          paymentTerms: 'شروط الدفع',
          validUntil: 'صالح حتى',
          selectDate: 'اختر التاريخ',
          dueOnInOneWeek: 'أسبوع',
          dueOnInTwoWeeks: 'أسبوعان',
          dueOnInOneMonth: 'شهر',
          dueOnInTwoMonths: 'شهران',
          dueOnInThreeMonths: '3 أشهر',
          dueOnInSixMonths: '6 أشهر',
          dueOnInOneYear: 'سنة',
          saveQuote: 'حفظ عرض السعر',
          quoteCreated: 'تم انشاء عرض السعر بنجاح',
          noItems: 'الرجاء اضافة عنصر واحد على الاقل',
          direct: 'مباشر',
          credit_sale: 'آجل',
          layaway: 'تقسيط',
          cash_on_delivery: 'الدفع عند الاستلام',
          costAndProfit: 'التكلفة والربح',
          cost: 'التكلفة',
          totalCost: 'إجمالي التكلفة (د.ع)',
          profitMargin: 'هامش الربح'
        }
      }
    })
    const headers = [t('itemName'), t('price'), t('quantity'), t('total')]

    const reps = ref<User[] | any>()
    const selectedRep = ref<any>()
    const isScanning = ref(false)

    watch(
      order,
      (newValue, _) => {
        if (props.localData) {
          vueCtx.emit('orderChanged', newValue)
        }
      },
      { deep: true }
    )

    function removeDineroKeys(obj) {
      const hasOwnProperty = Object.prototype.hasOwnProperty

      for (const key in obj) {
        if (hasOwnProperty.call(obj, key) && key.includes('Dinero')) {
          delete obj[key]
        }
      }
      return obj
    }

    onMounted(async () => {
      if (ctx.currentRegister?.id) {
        try {
          const terminals = await http.get<any[]>(`company/registers/${ctx.currentRegister.id}/terminals`)
          availableTerminals.value = (Array.isArray(terminals) ? terminals : []).filter((t: any) => t.enabled)
        } catch (e) {
          console.log('failed to load terminals', e)
        }
      }
    })

    const dueOnShortcuts = computed(() =>
      orderDueOnShortcutDates().map((shortcut) => ({
        text: t(shortcut.key),
        value: () =>
          orderDueOnShortcutDates().find((item) => item.key === shortcut.key)?.date ?? new Date()
      }))
    )

    onMounted(() => {
      if (props.localData) {
        if (props.isEdit) {
          order.value = props.localData as Order
          oldOrderPaidAmount.value = order.value.invoice.totalPaidAmount
          order.value.invoice.totalPaidAmount = 0

          // can't edit returned order
          let hasReturnedItem = false
          order.value.orderItems.forEach((item) => {
            if (item.returnedQuantity > 0) {
              hasReturnedItem = true
            }
          })

          if (hasReturnedItem) {
            ElMessage.error(t('canotEditReturnedOrder'))
            router.push(Route.SALES_ORDERS_ORDER.replace(':id', order.value.id as string))
            return
          }

          for (const oi of order.value.orderItems) {
            if (oi.item.isSerialized) {
              oi.serialIDs = oi.orderItemSerials.map((s) => s.itemSerial.id)
            }
            // Store original quantities so we can add them back to available stock during edit
            const existingQty = originalOrderQuantities.value.get(oi.itemID) || 0
            originalOrderQuantities.value.set(oi.itemID, existingQty + oi.quantity)
          }
        } else {
          order.value = Object.assign(
            new Order(),
            removeDineroKeys(JSON.parse(JSON.stringify(props.localData.order)))
          )
          order.value.totalPaidAmount = 0
          order.value.paidBeforeChangeAmount = 0
          order.value.invoice.totalPaidAmount = 0
        }

        // nextTick(() => {
        //   checkItemsStockQuantity()
        // })

        // console.log(order.value)
        if (order.value.customerID) {
          customerName.value = `${order.value.invoice.customer?.name}`
          customer.value = Customer.from(order.value.invoice.customer)
          customers.value = [Customer.from(order.value.invoice.customer)]
          fetchCustomerBalance(order.value.customerID)
        }
      }
    })

    function openSerialDialog(oi: OrderItem) {
      currentOrderItem.value = oi
      for (const item of currentOrderItem.value.item.itemStocks[0].itemSerials) {
        if (item.number == serialSearchTerm.value) {
          if (!currentOrderItem.value.serialIDs) {
            currentOrderItem.value.serialIDs = []
          }
          if (!currentOrderItem.value.serialIDs.includes(item.id)) {
            currentOrderItem.value.serialIDs.push(item.id)
            currentOrderItem.value = new OrderItem(new Item())
            insertSerialDialogVisible.value = false
            return
          }
        }
      }

      insertSerialDialogVisible.value = true
    }

    function closeSerialDialog() {
      currentOrderItem.value = new OrderItem(new Item())
      insertSerialDialogVisible.value = false
    }

    const handleScan = async (barcode: string) => {
      console.log('✅ Scanned:', barcode)

      if (!barcode || barcode.trim() === '') {
        return
      }

      try {
        const url = `${Item.ENDPOINT}?type=checkout&q=${barcode}`
        let items = await http.get<Item[]>(url)
        items = items.map((item) => Item.from(item))
        let temp = items.slice()

        // hide parent item and replace it with its variants
        for (let i = 0; i < items.length; i++) {
          if (items[i].hasVariants) {
            temp = temp.splice(i + 1, 1)
            temp.push(...items[i].variants.filter((i) => !i.disabled))
          }
        }

        items = temp

        // check if this barcode or sku are packs related so we can select them
        let itemPack = null
        if (items.length && Array.isArray(items[0].itemPacks)) {
          for (const ip of items[0].itemPacks) {
            if (ip.barcode == barcode || ip.sku == barcode) {
              itemPack = ip
            }
          }
        }

        if (itemPack) {
          currentItemPack.value = itemPack
        } else {
          currentItemPack.value = null
        }

        // Handle the scan results
        if (items.length === 1) {
          // Single item found - add it directly
          if (settingsStore.getValue('scan_beep') == 'true') {
            const successAudio = new Audio(successbeep)
            successAudio.play()
          }
          addItem(items[0])
        } else if (items.length === 0) {
          // No items found - show error message
          ElMessage.error({
            dangerouslyUseHTMLString: true,
            message: `<h2>${t('itemNotFound')}</h2>`
          })
          if (settingsStore.getValue('scan_error_beep') == 'true') {
            const errorAudio = new Audio(errorbeep)
            errorAudio.play()
          }
        } else {
          // Multiple items found - you might want to show them in the search dropdown
          // For now, we'll just take the first one or you could modify this behavior
          if (settingsStore.getValue('scan_beep') == 'true') {
            const successAudio = new Audio(successbeep)
            successAudio.play()
          }
          addItem(items[0])
        }
      } catch (error) {
        console.error('Error searching for scanned item:', error)
        ElMessage.error({
          dangerouslyUseHTMLString: true,
          message: `<h2>${t('itemNotFound')}</h2>`
        })
        if (settingsStore.getValue('scan_error_beep') == 'true') {
          const errorAudio = new Audio(errorbeep)
          errorAudio.play()
        }
      }
    }

    function addItem(selected: Item) {
      itemSearchTerm.value = ''
      const tmpItemPack = currentItemPack.value
      currentItemPack.value = null

      // Allow adding items even if out of stock - will validate during submission
      // Items with zero stock can be added as SPO (Special Purchase Order)

      let oi: OrderItem | null
      if (tmpItemPack) {
        if (tmpItemPack.disabled) {
          return
        }
        if (selected.isSerialized && !isOrderFulfillmentMode.value) {
          alertModal.showDanger({
            title: t('cantSellItemPackWithSerializedItem')
          })
          return
        }
        oi = order.value.addItemPack(selected, tmpItemPack, appendToOrderItems.value)
      } else {
        oi = order.value.addItem(selected, appendToOrderItems.value)
      }

      // Only open serial dialog if not in fulfillment mode, not quote mode, and item is not expirable
      if (selected.isSerialized && !isOrderFulfillmentMode.value && !props.isQuote && !selected.isExpirable) {
        openSerialDialog(oi)
      }
      serialSearchTerm.value = ''
      appendToOrderItems.value = false
      updateOrderItemCustomPrice(customer.value, oi)
    }

    async function searchItems(query: string, cb: Function) {
      setTimeout(() => {
        console.log('isScanning', isScanning.value)
      }, 1000)
      searchLoading.value = true
      serialSearchTerm.value = query
      if (pressedEnter.value) {
        itemSearchTerm.value = ''
      }
      // if an item already exists in the table, don't do remote search
      // const cachedItem = order.value.findByBarcode(query) || order.value.findBySKU(query)
      // if (cachedItem) {
      //   addItem(cachedItem)
      //   cb([])
      //   return
      // }
      try {
        const url = `${Item.ENDPOINT}?type=checkout&q=${query}`
        let items = await http.get<Item[]>(url)
        items = items.map((item) => Item.from(item))
        let temp = items.slice()

        // hide parent item and replace it with its variants
        for (let i = 0; i < items.length; i++) {
          if (items[i].hasVariants) {
            temp = temp.splice(i + 1, 1)
            // temp = [...temp.slice(0, i), ...temp.slice(i + 1)];

            temp.push(...items[i].variants.filter((i) => !i.disabled))
          }
        }

        items = temp
        tempSearchResult.value = temp

        // we need to check if this barcode or sku are packs related so we can select them
        let itemPack = null
        if (items.length && Array.isArray(items[0].itemPacks)) {
          for (const ip of items[0].itemPacks) {
            if (ip.barcode == query || ip.sku == query) {
              itemPack = ip
            }
          }
        }

        if (itemPack) {
          currentItemPack.value = itemPack
        } else {
          currentItemPack.value = null
        }

        // when entering a barcode, the scanner hits enter by default
        // so if we get back one result, we wanna append that right away
        // it makes the process much faster than make the user select by themselves
        if (pressedEnter.value && items.length === 1) {
          if (settingsStore.getValue('scan_beep') == 'true') {
            const errorAudio = new Audio(successbeep)
            errorAudio.play()
          }
          addItem(items[0])
          cb([])
          tempSearchResult.value = null
        } else if (pressedEnter.value && items.length == 0) {
          ElMessage.error({
            dangerouslyUseHTMLString: true,
            message: `<h2>${t('itemNotFound')}</h2>`
          })
          if (settingsStore.getValue('scan_error_beep') == 'true') {
            const errorAudio = new Audio(errorbeep)
            errorAudio.play()
          }
          cb([])
          tempSearchResult.value = null
        } else {
          // show them all options
          cb(items)
        }
      } catch (error) {
        alertModal.showDanger({ title: error.title })
        console.log(error)

        cb([])
      }
      pressedEnter.value = false
      searchLoading.value = false
    }

    function isOrderValid() {
      // Quote mode: only validate that there's at least one item
      if (props.isQuote) {
        if (order.value.orderItems.length === 0) {
          error.value.title = t('noItems')
          return false
        }
        return true
      }

      if (useSettingsStore().getValue('order_fulfilling_module') == 'true') {
        return true
      }

      const errors: string[] = []
      if (order.value.customerID === '' && order.value.paymentType !== 'direct') {
        errors.push(t('noCustomer'))
      }

      if (order.value.customerID === '' && order.value.invoice.insuranceID) {
        errors.push(t('noCustomer'))
      }

      if (
        order.value.invoice.reqOnlyTotalCashPaidAmount == 0 &&
        order.value.invoice.reqOnlyTotalEpaymentAmount == 0 &&
        order.value.invoice.reqOnlyTotalCreditPaidAmount == 0 &&
        order.value.paymentType === 'layaway' &&
        !props.isEdit
      ) {
        errors.push(t('minimumHasToBePaid'))
      }

      const isSalesRepRequired = settingsStore.getValue('sale_rep_required') == 'true' // JSON.parse(localStorage.getItem("is_sales_rep_required") || 'false')
      if (isSalesRepRequired && !order.value.invoice.salesRepID) {
        errors.push(t('noSalesRep'))
      }

      order.value.orderItems.forEach((oi) => {
        // Skip serial validation for fulfillment mode
        if (
          !isOrderFulfillmentMode.value &&
          oi.item?.isSerialized &&
          oi.serialIDs.length !== oi.quantity
        ) {
          const errMsg = `${oi.item.name}: ${t('validation.matchingLength', {
            thisField: localizeFieldName('lenSerialNumbers', i18nOrderMessages),
            thatField: localizeFieldName('quantity', i18nOrderMessages)
          })}`
          if (!oi.specialOrderItemInfo) errors.push(errMsg)
        }

        // Check for out of stock items without SPO
        if (!isOrderFulfillmentMode.value && isOutOfStock(oi.item, oi.quantity) && !oi.specialOrderItemInfo) {
          const availableQty = getAvailableQuantity(oi.item)
          errors.push(
            t('quantityExceedsTheStockQuantity', {
              quantity: availableQty,
              name: oi.item.name
            })
          )
        }

        if (oi.specialOrderItemInfo && oi.specialOrderItemInfo.vendor) {
          if (order.value.customerID === '') {
            errors.push(t('noCustomer'))
          }
          return false
        }
      })

      if (errors.length) {
        error.value.title = t('validation.inputErrors')
        error.value.body = errors.join('\n')
        checkoutDrawerOpen.value = false

        return false
      }

      return true
    }

    async function submitQuote() {
      if (!isOrderValid()) {
        return
      }

      if (isSubmitting.value) {
        return
      }

      isSubmitting.value = true
      error.value.title = ''
      error.value.body = ''

      try {
        const currency = order.value.currency || ctx.currency || 'IQD'
        const precision = order.value.precision || 0

        const orderItems = order.value.orderItems.map((oi: OrderItem) => {
          const orderItemSerials = oi.item.isSerialized
            ? oi.serialIDs.map((serialId: string) => ({ itemSerialID: serialId }))
            : []
          return {
            itemID: oi.item.id,
            itemSalePriceAmount: oi.itemSalePriceAmount,
            quantity: oi.quantity,
            notes: oi.notes || '',
            packInfo: oi.packInfo,
            customPriceInfo: oi.customPriceInfo,
            itemDiscountType: oi.itemDiscountType,
            itemDiscountAmount: oi.itemDiscountAmount,
            itemDiscountTotal: oi.itemDiscountTotal,
            orderItemSerials
          }
        })

        // Compute totals
        const subtotal = order.value.computeTotal().getAmount()
        const totalAfterDiscount = order.value.computeTotalAfterDiscount().getAmount()
        const totalDiscountAmount = subtotal - totalAfterDiscount

        const payload = {
          orderType: 'quote',
          validUntil: quoteValidUntil.value || null,
          invoice: {
            customerID: order.value.customerID || null,
            currency: currency,
            precision: precision,
            paymentType: quotePaymentType.value,
            totalBeforeDiscounts: subtotal,
            totalDiscountAmount: totalDiscountAmount,
            totalDueAmount: totalAfterDiscount,
            totalPaidAmount: 0,
            totalBalanceAmount: totalAfterDiscount,
            notes: order.value.invoice.notes || '',
            insuranceTotalDueAmount: 0,
            insuranceTotalPaidAmount: 0,
            insuranceTotalBalanceAmount: 0,
            shippingFeeAmount: 0
          },
          orderItems: orderItems
        }

        const result = await http.post<Order>('/sales/quotes', payload)
        alertModal.showSuccess({ title: t('quoteCreated') })
        await router.push(
          Route.SALES_QUOTES_QUOTE.replace(':id', result.id || '')
        )
      } catch (err: any) {
        error.value.title = err.title || 'Error'
        error.value.body = err.body || ''
      } finally {
        isSubmitting.value = false
      }
    }

    function openQuoteDialog() {
      quoteDrawerOpen.value = true
    }

    function openCustomerDialog() {
      ctx.getDir() === 'rtl' ? (direction.value = 'rtl') : (direction.value = 'ltr')

      customerDialogOpen.value = true
    }

    function openEditCustomerDialog() {
      if (!customer.value?.id) return
      editCustomerDialogVisiable.value = true
    }

    function closeCustomerDialog() {
      if (selectedIndex.value == undefined) {
        customerName.value = ''
        customer.value = null
        customerBalance.value = 0
        customerCredit.value = 0
        order.value.invoice.customer = undefined
        order.value.customerID = undefined
        vueCtx.emit('selectCustomer', null)
      } else if (selectedIndex.value >= 0) {
        customerName.value = customers.value[selectedIndex.value].contactDisplayName
        customer.value = customers.value[selectedIndex.value]
        order.value.customerID = customers.value[selectedIndex.value].id
        order.value.invoice.customer = customer.value
        vueCtx.emit('selectCustomer', customerName.value)
        updateOrderItemsCustomPrice(customer.value)
        fetchCustomerBalance(customers.value[selectedIndex.value].id)
      } else {
        customerName.value = ''
        customer.value = null
        customerBalance.value = 0
        customerCredit.value = 0
        order.value.invoice.customer = undefined
        order.value.customerID = undefined
        vueCtx.emit('selectCustomer', null)
      }

      customerDialogOpen.value = false
    }

    async function fetchCustomerBalance(customerId: string) {
      try {
        const url = `${Customer.INVOICES_ENDPOINT}${customerId}`
        const res = await http.get<{ totalBalanceAmount: number; totalStoreCreditBalance: number }>(url)
        customerBalance.value = res?.totalBalanceAmount || 0
        customerCredit.value = res?.totalStoreCreditBalance || 0
      } catch {
        customerBalance.value = 0
        customerCredit.value = 0
      }
    }

    function closeDiscountDialog() {
      discountDialogOpen.value = false
    }

    function cancelDiscount() {
      discountDialogOpen.value = false
      order.value.discountByAmount = 0
      order.value.discountByPercentage = 0
      order.value.applyDiscountToAll = false
    }

    function showDiscountDialog() {
      discountDialogOpen.value = true
      order.value.discountType = order.value.discountType || 'amount'
      setTimeout(() => {
        const inputRef = order.value.discountType === 'percentage' ? discountPercentageInput : discountAmountInput
        const input = inputRef.value?.$el.querySelector('input')
        input?.focus()
        input?.select()
      }, 100)
    }

    function openDiscountDialog() {
      // Users with the "apply discount" permission go straight to the dialog. Others
      // must first enter a valid discount code (which unlocks it for this order).
      if (can('create', '/sales/applydiscount') || discountUnlocked.value) {
        showDiscountDialog()
        return
      }
      discountCodeError.value = ''
      discountCodeInput.value = ''
      discountCodeDialogOpen.value = true
      setTimeout(() => {
        discountCodeInputRef.value?.$el?.querySelector('input')?.focus()
      }, 100)
    }

    function closeDiscountCodeDialog() {
      discountCodeDialogOpen.value = false
    }

    async function confirmDiscountCode() {
      const code = (discountCodeInput.value || '').replace(/\D/g, '')
      if (code.length !== 9) {
        discountCodeError.value = t('discountCodeInvalidFormat')
        return
      }
      discountCodeChecking.value = true
      discountCodeError.value = ''
      try {
        await http.post('/sales/orders/validate-discount-code', {
          code,
          locationID: (ctx.currentLocation as Location)?.id ?? ''
        })
        // Code is valid + unused: remember it on the order and unlock the dialog.
        order.value.discountCode = code
        discountUnlocked.value = true
        discountCodeDialogOpen.value = false
        showDiscountDialog()
      } catch (err) {
        discountCodeError.value = err?.body || err?.title || t('discountCodeInvalid')
      } finally {
        discountCodeChecking.value = false
      }
    }

    async function searchCustomers(query: string) {
      if (!query) {
        query = ''
      }
      customerSearchTerm.value = query.trim()
      try {
        const url = `${Customer.ENDPOINT}?q=${query}`
        const customersRes = await http.get<Customer[]>(url)
        customers.value = customersRes.map((customer) => Customer.from(customer))
      } catch (error) {
        alertModal.showDanger({ title: error.title })
        customers.value = []
      }
    }

    function openNewCustomer() {
      customerSelectRef.value?.blur?.()
      customerDialogOpen.value = false
      newCustomerDialogVisiable.value = true
    }

    function onCustomerNameChange(value: string) {
      selectedIndex.value = customers.value.findIndex((customer) => customer.id === value)
    }

    function getDineroValue(amount: number, currency: Currency, precision: number) {
      const dinero = Dinero({
        amount,
        currency,
        precision
      })

      return formatDinero(dinero)
    }

    function getItemTotal(orderItem: OrderItem) {
      const total =
        (orderItem.quantity as number) *
        (orderItem.itemSalePriceAmount ? orderItem.itemSalePriceAmount : 1)
      return getDineroValue(total, orderItem.currency, orderItem.precision)
    }

    function getTotalItemsCount() {
      const totalCount = order.value.orderItems.reduce((acc, item) => {
        return acc + item.quantity
      }, 0)

      return totalCount
    }

    function getTotalItemDiscounts() {
      if (order.value.orderItems.length === 0) {
        return Dinero({
          amount: 0,
          currency: ctx.currency,
          precision: 0
        })
      }

      const totalItemDiscounts = order.value.orderItems.reduce((acc, item) => {
        return acc + (item.itemDiscountTotal * item.quantity)
      }, 0)

      return Dinero({
        amount: totalItemDiscounts ?? 0,
        currency: ctx.currency,
        precision: order.value.precision
      })
    }

    function getTotalCost() {
      if (order.value.orderItems.length === 0) {
        return Dinero({ amount: 0, currency: ctx.currency, precision: 0 })
      }
      const totalCost = order.value.orderItems.reduce((acc, item) => {
        return acc + (item.item.currentDynamicCostAmount || 0) * item.quantity
      }, 0)
      return Dinero({ amount: totalCost, currency: ctx.currency, precision: order.value.precision })
    }

    function getProfitMargin() {
      const revenue = order.value.computeTotalAfterDiscount().getAmount()
      if (revenue === 0) return 0
      const cost = getTotalCost().getAmount()
      return ((revenue - cost) / revenue) * 100
    }

    function handleCancelOrder() {
      if (hasOrderChanges.value) {
        cancelOrderDialogOpen.value = true
      } else {
        confirmCancelOrder()
      }
    }

    function confirmCancelOrder() {
      cancelOrderDialogOpen.value = false
      if (props.isEdit) {
        router.push(Route.SALES_ORDERS_ORDER.replace(':id', order.value?.id as string))
      } else {
        resetOrder()
      }
    }

    function resetOrder() {
      order.value = new Order()
      selectedIndex.value = null
      customerName.value = ''
      customer.value = null
      // order.value.empty();
      pressedEnter.value = false
      itemSearchTerm.value = ''
      error.value = { title: '', body: '' }

      currentOrderItem.value = new OrderItem(new Item())
      insertSerialDialogVisible.value = false

      customerDialogOpen.value = false
      customers.value = new Array<Customer>()

      checkoutDrawerOpen.value = false
      quoteDrawerOpen.value = false
    }

    function isValidPayment() {
      const totalPaid =
        order.value.invoice.reqOnlyTotalCashPaidAmount +
        order.value.invoice.reqOnlyTotalEpaymentAmount +
        order.value.invoice.reqOnlyTotalCreditPaidAmount

      if (
        order.value.invoice.reqOnlyTotalEpaymentAmount > 0 &&
        availableTerminals.value.length > 0 &&
        !order.value.invoice.reqOnlyTerminalID
      ) {
        terminalError.value = true
        cashierPinDialogOpen.value = false
        return false
      }

      if (
        order.value.paymentType === 'direct' &&
        order.value.totalDueAmount > totalPaid &&
        !props.isEdit
      ) {
        error.value.title = t('errPaymentMustMatch')
        checkoutDrawerOpen.value = false
        cashierPinDialogOpen.value = false

        return false
      }

      return true
    }

    async function openCashierPinDialog(casherInputRef: any) {
      // for validation purposes
      order.value.preparePayload(
        ctx.currentUser as User,
        ctx.currentRegister as Register,
        ctx.currentLocation as Location
      )

      if (!isOrderValid() || !isValidPayment()) {
        return
      }

      if (useSettingsStore().getValue('is_cashier_pin_required') == 'true') {
        cashierPinDialogOpen.value = true

        nextTick(() => {
          setTimeout(() => {
            casherInputRef().focus()
          }, 0)
        })
        return
      }
      submitOrder()
    }

    async function submitOrder() {
      if (useSettingsStore().getValue('is_cashier_pin_required') == 'true') {
        if (cashierPin.value === '') {
          error.value.title = t('errCashierPinRequired')
          cashierPinDialogOpen.value = false
          checkoutDrawerOpen.value = false
          return
        }
      }

      // Prevent double submission
      if (isSubmitting.value) {
        return
      }

      const requestData = order.value.preparePayload(
        ctx.currentUser as User,
        ctx.currentRegister as Register,
        ctx.currentLocation as Location
      )

      if (!isOrderValid() || !isValidPayment()) {
        return
      } else {
        error.value.title = ''
        error.value.body = ''
        cashierPinDialogOpen.value = false
        isSubmitting.value = true

        try {
          let o: Order
          if (props.isEdit) {
            o = await http.put<Order>(Order.ENDPOINT + '/' + order.value.id, requestData, {
              headers: { pin: cashierPin.value }
            })
          } else {
            o = await http.post<Order>(Order.ENDPOINT, requestData, {
              headers: { pin: cashierPin.value }
            })
          }

          alertModal.showSuccess({ title: t('successMsg') })
          if (props.localData) {
            vueCtx.emit('orderSold', order.value)
            setTimeout(async () => {
              await router.push(Route.SALES_ORDERS_ORDER_RECEIPT.replace(':id', o.id || ''))
            }, 200)
          } else {
            await router.push(Route.SALES_ORDERS_ORDER_RECEIPT.replace(':id', o.id || ''))
          }
        } catch (err) {
          error.value.title = err.title
          error.value.body = err.body
          checkoutDrawerOpen.value = false
        } finally {
          isSubmitting.value = false
        }
      }
    }

    function indexMethod(index: number) {
      return index + 1
    }

    async function onCustomerCreater(_customer: Customer) {
      order.value.customerID = _customer.id || ''
      customer.value = _customer
      customerName.value = `${_customer.name}`
      order.value.invoice.customer = _customer
      newCustomerDialogVisiable.value = false
      editCustomerDialogVisiable.value = false
      customerSearchTerm.value = ''
      if (_customer.id) fetchCustomerBalance(_customer.id)
    }

    async function searchReps(query: string) {
      if (!query) {
        query = ''
      }
      try {
        const url = `${User.ENDPOINT}/sales-reps`
        let res = await http.get<User[]>(url)
        if (query) {
          res = res.filter((rep) => rep.name.toLowerCase().includes(query.toLowerCase()))
        }
        reps.value = res.map((rep) => User.from(rep))
      } catch (error) {
        reps.value = []
      }
    }

    function clearCustomer() {
      customerName.value = ''
      order.value.customerID = ''
      selectedIndex.value = null
      customer.value = null
      customerBalance.value = 0
      customerCredit.value = 0
      order.value.invoice.customer = undefined
      vueCtx.emit('selectCustomer', null)
    }

    function onPack(packInfo: any, oi: OrderItem) {
      oi.packInfo = packInfo

      // Clear item discount when pack changes
      oi.itemDiscountType = undefined
      oi.itemDiscountAmount = 0
      oi.itemDiscountTotal = 0

      if (packInfo == null) {
        oi.itemSalePriceAmount = oi.item.salePriceAmount
        setTimeout(() => {
          // trigger custom price update
          updateOrderItemsCustomPrice(customer.value)
        }, 100)
        return
      }

      oi.itemSalePriceAmount = packInfo.itemPack.salePriceAmount

      setTimeout(() => {
        // trigger custom price update
        updateOrderItemsCustomPrice(customer.value)
      }, 100)
    }

    function onCustomPriceInfo(customPriceInfo: any, oi: OrderItem) {
      oi.customPriceInfo = customPriceInfo

      // Clear item discount when custom price changes
      oi.itemDiscountType = undefined
      oi.itemDiscountAmount = 0
      oi.itemDiscountTotal = 0

      if (customPriceInfo == null) {
        if (oi.packInfo) {
          oi.itemSalePriceAmount = oi.packInfo.salePriceAmount
        } else {
          oi.itemSalePriceAmount = oi.item.salePriceAmount
        }
        return
      }
      oi.itemSalePriceAmount = customPriceInfo.salePriceAmount
    }

    function onItemDiscount(
      discountInfo: { discountType: string; discountAmount: number; discountTotal: number } | null,
      oi: OrderItem
    ) {
      // Get the base price (before any item discount)
      // If discount was already applied, add back the old discountTotal to get base price
      const basePrice = oi.itemSalePriceAmount + (oi.itemDiscountTotal || 0)

      if (discountInfo == null) {
        // Clear discount - restore base price
        oi.itemDiscountType = undefined
        oi.itemDiscountAmount = 0
        oi.itemDiscountTotal = 0
        oi.itemSalePriceAmount = basePrice
      } else {
        // Apply discount
        oi.itemDiscountType = discountInfo.discountType as 'percentage' | 'amount'
        oi.itemDiscountAmount = discountInfo.discountAmount
        oi.itemDiscountTotal = discountInfo.discountTotal
        oi.itemSalePriceAmount = Math.max(basePrice - discountInfo.discountTotal, 0)
      }
    }

    function itemQuantityChanged(e: any, data: any) {
      // Skip quantity validation for fulfillment mode
      if (isOrderFulfillmentMode.value) {
        return
      }

      // Skip validation if item has unlimited stock or sell out of stock enabled
      if (data.item.unlimitedStock || data.item.sellOutOfStock) {
        stockQuantityErrors.value.delete(data.item.name)
        return
      }

      const availableQty = getAvailableQuantity(data.item)
      if (availableQty < e) {
        stockQuantityErrors.value.set(
          data.item.name,
          t('quantityExceedsTheStockQuantity', { quantity: availableQty, name: data.item.name })
        )
        return
      }
      stockQuantityErrors.value.delete(data.item.name)
    }

    function checkItemsStockQuantity() {
      // Skip quantity validation for fulfillment mode
      if (isOrderFulfillmentMode.value) {
        return
      }

      for (let oi of order.value.orderItems) {
        // Skip validation if item is marked as SPO or has unlimited stock or sell out of stock
        if (oi.specialOrderItemInfo || oi.item.unlimitedStock || oi.item.sellOutOfStock) {
          stockQuantityErrors.value.delete(oi.item.name)
          continue
        }

        const availableQty = getAvailableQuantity(oi.item)
        if (availableQty < oi.quantity) {
          stockQuantityErrors.value.set(
            oi.item.name,
            t('quantityExceedsTheStockQuantity', { quantity: availableQty, name: oi.item.name })
          )
          continue
        }
        stockQuantityErrors.value.delete(oi.item.name)
      }
    }

    function updateOrderItemsCustomPrice(val: Customer) {
      if (!val) {
        return
      }
      for (let i = 0; i < order.value.orderItems.length; i++) {
        if (order.value.orderItems[i].packInfo && order.value.orderItems[i].packInfo?.packID) {
          if (order.value.orderItems[i].packInfo?.itemPack)
            for (const ipcp of order.value.orderItems[i].packInfo?.itemPack.itemPackCustomPrices) {
              if (ipcp.customPriceTypeID == val.customerType && !ipcp.disabled) {
                onCustomPriceInfo(
                  {
                    customPriceTypeID: ipcp.customPriceTypeID,
                    type: 'pack',
                    itemCustomPriceID: null,
                    itemCustomPrice: null,
                    itemPackCustomPriceID: ipcp.id,
                    itemPackCustomPrice: ipcp,
                    salePriceAmount: ipcp.salePriceAmount
                  },
                  order.value.orderItems[i]
                )
              }
            }
        } else {
          for (const icp of order.value.orderItems[i].item.itemCustomPrices) {
            if (icp.customPriceTypeID == val.customerType && !icp.disabled) {
              onCustomPriceInfo(
                {
                  customPriceTypeID: icp.customPriceTypeID,
                  type: 'item',
                  itemCustomPriceID: icp.id,
                  itemCustomPrice: icp,
                  itemPackCustomPriceID: null,
                  itemPackCustomPrice: null,
                  salePriceAmount: icp.salePriceAmount
                },
                order.value.orderItems[i]
              )
            }
          }
        }
      }
    }
    function updateOrderItemCustomPrice(val: Customer, oi: OrderItem = null) {
      if (!val || !oi) {
        return
      }
      if (oi.packInfo) {
        if (oi.packInfo.itemPack)
          for (const ipcp of oi.packInfo.itemPack.itemPackCustomPrices) {
            if (ipcp.customPriceTypeID == val.customerType && !ipcp.disabled) {
              onCustomPriceInfo(
                {
                  customPriceTypeID: ipcp.customPriceTypeID,
                  type: 'pack',
                  itemCustomPriceID: null,
                  itemCustomPrice: null,
                  itemPackCustomPriceID: ipcp.id,
                  itemPackCustomPrice: ipcp,
                  salePriceAmount: ipcp.salePriceAmount
                },
                oi
              )
            }
          }
      } else {
        for (const icp of oi.item.itemCustomPrices) {
          if (icp.customPriceTypeID == val.customerType && !icp.disabled) {
            onCustomPriceInfo(
              {
                customPriceTypeID: icp.customPriceTypeID,
                type: 'item',
                itemCustomPriceID: icp.id,
                itemCustomPrice: icp,
                itemPackCustomPriceID: null,
                itemPackCustomPrice: null,
                salePriceAmount: icp.salePriceAmount
              },
              oi
            )
          }
        }
      }
    }

    function onSearchSelectPressedEnter() {
      console.log('isScanning', isScanning.value)
      pressedEnter.value = true
      setTimeout(() => {
        if (tempSearchResult.value?.length == 0) {
          ElMessage.error({
            dangerouslyUseHTMLString: true,
            message: `<h2>${t('itemNotFound')}</h2>`
          })
          if (settingsStore.getValue('scan_error_beep') == 'true') {
            const errorAudio = new Audio(errorbeep)
            errorAudio.play()
          }
        }
      }, 500)
    }

    function onSearchSelectKeyupEnter() {
      console.log('isScanning', isScanning.value)
      setTimeout(() => {
        if (!searchLoading.value) {
          pressedEnter.value = false
        }
      }, 400)
    }

    function onSpecialOrderItemSubmitted(oi: OrderItem, data: any) {
      if (data == null) {
        if (table.value?.getSelectionRows().length > 0) {
          for (const goi of table.value?.getSelectionRows() as OrderItem[]) {
            goi.specialOrderItemInfo = data
          }
        } else {
          oi.specialOrderItemInfo = data
        }
        return
      }

      if (data.groupID && table.value?.getSelectionRows().length > 0) {
        for (const goi of table.value?.getSelectionRows() as OrderItem[]) {
          goi.specialOrderItemInfo = data
        }
        table.value?.clearSelection()
      } else {
        oi.specialOrderItemInfo = data
      }
    }

    function onInsurance(e: any) {
      if (e) {
        order.value.invoice.insuranceID = e.insurance
        order.value.invoice.insuranceTotalDueAmount = e.amountToBePaid
        return
      }

      order.value.invoice.insuranceID = undefined
      order.value.invoice.insuranceTotalDueAmount = 0
    }

    function getLocationStock(item: Item): Stock {
      if (item.currentLocationStock?.quantity != null && item.currentLocationStock.quantity !== 0) {
        return item.currentLocationStock
      }
      const locationId = ctx.currentLocation?.id
      if (locationId && item.itemStocks?.length) {
        const match = item.itemStocks.find((s: any) => s.locationID === locationId)
        if (match) return match
      }
      return item.currentLocationStock || new Stock()
    }

    function getAvailableQuantity(item: Item) {
      if (item.unlimitedStock) {
        return t('unlimited')
      }

      const stock = getLocationStock(item)
      let currentQuantity = stock.quantity || 0
      let pendingQuantity = stock.pendingQuantity || 0

      // For expirable items, calculate from all stocks
      if (item.isExpirable && item.itemStocks) {
        currentQuantity = 0
        pendingQuantity = 0
        item.itemStocks.forEach((stock: any) => {
          currentQuantity += stock.quantity || 0
          pendingQuantity += stock.pendingQuantity || 0
        })
      }

      let available = currentQuantity - pendingQuantity

      // When editing an order, add back the original order quantity for this item
      // since those items are already part of this order and should be available
      if (props.isEdit && item.id) {
        const originalQty = originalOrderQuantities.value.get(item.id) || 0
        available += originalQty
      }

      return available
    }

    function isOutOfStock(item: Item, quantity: number): boolean {
      if (item.unlimitedStock || item.sellOutOfStock) {
        return false
      }
      const availableQty = getAvailableQuantity(item)
      return typeof availableQty === 'number' && availableQty < quantity
    }

    function getOrderItemSerials(oi: OrderItem) {
      if (oi.extraProperties?.batchNumber) {
        for (const stock of oi.item.itemStocks) {
          if (stock.extraProperties?.batchNumber == oi.extraProperties?.batchNumber) {
            return stock.itemSerials
          }
        }
      } else {
        return oi.item.itemStocks[0].itemSerials
      }
      return []
    }

    function onOrderTypeChange(orderType: string) {
      // Set payment type to direct if order type is direct
      if (orderType === 'direct') {
        order.value.paymentType = 'direct'
      }

      // Clear serial and batch selections when switching to delivery/pickup (fulfillment mode)
      if (
        (orderType === 'delivery' || orderType === 'pick_up') &&
        useSettingsStore().getValue('order_fulfilling_module') == 'true'
      ) {
        for (const oi of order.value.orderItems) {
          if (oi.serialIDs?.length) {
            oi.serialIDs = []
          }
          if (oi.extraProperties?.batchNumber) {
            oi.extraProperties.batchNumber = undefined
            oi.extraProperties.expiryDate = undefined
            oi.extraProperties.manufactureDate = undefined
            oi.extraProperties.stockID = undefined
          }
        }
      }

      // Open customer dialog if order type is delivery or pickup and no customer is selected
      if (
        (orderType === 'delivery' || orderType === 'pick_up') &&
        (!order.value.customerID || order.value.customerID === '')
      ) {
        openCustomerDialog()
      }
    }

    function onBatchNumberChange(orderItem: OrderItem) {
      // Clear selected serials when batch number changes
      if (orderItem.serialIDs && orderItem.serialIDs.length > 0) {
        orderItem.serialIDs = []
      }
    }

    // Track if order has been modified
    const hasOrderChanges = computed(() => {
      return (
        order.value.orderItems.length > 0 ||
        (order.value.customerID !== undefined && order.value.customerID !== '' && order.value.customerID !== null) ||
        customerName.value !== '' ||
        (order.value.invoice.notes !== '' && order.value.invoice.notes !== undefined && order.value.invoice.notes !== null) ||
        order.value.discountByAmount > 0 ||
        order.value.discountByPercentage > 0
      )
    })

    // Watch for changes and emit to parent
    watch(
      hasOrderChanges,
      (newValue) => {
        vueCtx.emit('hasChanges', newValue)
      },
      { immediate: true }
    )

    return {
      settingsStore,
      getOrderItemSerials,
      editBalance,
      dueOnDisabled,
      dueOnShortcuts,
      isOrderFulfillmentMode,
      isScanning,
      handleScan,
      onInsurance,
      deliveries,
      OrderItem,
      Item,
      table,
      ctx,
      onSpecialOrderItemSubmitted,
      onPack,
      pressedEnter,
      itemSearchTerm,
      selectedIndex,
      order,
      searchItems,
      addItem,
      headers,
      data,
      checkoutDrawerOpen,
      refundDrawerOpen,
      paymentType,
      http,
      alertModal,
      t,
      error,
      stockQuantityErrors,
      loading,
      router,
      route,
      customerDialogOpen,
      newCustomerDialogVisiable,
      customerSearchTerm,
      customerSelectRef,
      openNewCustomer,
      editCustomerDialogVisiable,
      customers,
      customerName,
      customerBalance,
      customerCredit,
      openCustomerDialog,
      openEditCustomerDialog,
      closeCustomerDialog,
      onCustomerNameChange,
      searchCustomers,
      direction,
      currentOrderItem,
      insertSerialDialogVisible,
      closeSerialDialog,
      openSerialDialog,
      getDineroValue,
      getItemTotal,
      discountDialogOpen,
      discountCodeDialogOpen,
      discountCodeInput,
      discountCodeError,
      discountCodeChecking,
      discountCodeInputRef,
      confirmDiscountCode,
      closeDiscountCodeDialog,
      canOverrideDiscount,
      closeDiscountDialog,
      cashierPinDialogOpen,
      cashierPin,
      // cashierPinInput,
      openCashierPinDialog,
      openDiscountDialog,
      canApplyDiscount,
      getTotalItemsCount,
      getTotalItemDiscounts,
      formatDinero,
      formatMoney,
      resetOrder,
      submitOrder,
      quoteDrawerOpen,
      openQuoteDialog,
      cancelDiscount,
      searchInput,
      discountPercentageInput,
      discountAmountInput,
      indexMethod,
      onCustomerCreater,
      isOrderValid,
      customer,
      Search,
      top8Items,
      reps,
      selectedRep,
      searchReps,
      clearCustomer,
      appendToOrderItems,
      itemQuantityChanged,
      onCustomPriceInfo,
      onItemDiscount,
      updateOrderItemsCustomPrice,
      updateOrderItemCustomPrice,
      onSearchSelectPressedEnter,
      onSearchSelectKeyupEnter,
      orderTypes,
      oldOrderPaidAmount,
      onOrderTypeChange,
      onBatchNumberChange,
      getAvailableQuantity,
      isOutOfStock,
      hasOrderChanges,
      cancelOrderDialogOpen,
      handleCancelOrder,
      confirmCancelOrder,
      quoteValidUntil,
      quotePaymentType,
      submitQuote,
      showCostAndProfit,
      getTotalCost,
      getProfitMargin,
      availableTerminals,
      terminalError
    }
  },
  methods: {
    getCasherInputRef() {
      /* @ts-ignore */
      return this.$refs['cashierPinInput']
    },
    openCheckoutDialog() {
      this.$nextTick(() => {
        /* @ts-ignore */
        const paymentInput = this.$refs['paymentInput']
        if (paymentInput) {
          /* @ts-ignore */
          paymentInput.cashReceivedInputFocus()
        }
      })
      // order.value.prepareForCheckout();
      if (!this.isOrderValid()) {
        this.checkoutDrawerOpen = false
      }
    }
  }
})
</script>

<style>
@import url(../../assets/css/index.css);

.add-typed-customer {
  @apply flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold text-start;
  color: var(--el-color-primary);
}

.add-typed-customer:hover {
  background: var(--el-fill-color-light);
}

.bg-primary {
  background-color: var(--primary-bg-color);
}

.border-primary {
  border: 1px solid var(--primary-bg-color);
}

.text-primary {
  color: var(--primary-bg-color);
}

.bg-light-blue {
  background-color: #f2f7ff;
}

.item-name {
  width: 50%;
}

.item-price {
  height: inherit;
  width: auto;
}

.item-quantity {
  width: 15%;
}

.item-total {
  width: 15%;
}

.item-delete {
  width: 5%;
}

.el-radio-button__inner {
  width: 100% !important;
  font-size: 1.2rem;
}

.items-table {
  height: 65vh !important;
}

.el-drawer__header {
  padding-top: 5px;
}

.item-serial {
  height: 20px;
  width: 65px;
}
</style>
